import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { useZones } from "../hooks/useZones";
import { useBuildings } from "../hooks/useBuildings";
import { useCompanyLogos } from "../hooks/useCompanyLogos";
import { useUsers } from "../hooks/useUsers";
import { uploadCompanyLogo, setActiveLogo, deleteCompanyLogo, updateCompany, verifyCompanyQuickBooksConnection } from "../services/logoService";
import { Settings as SettingsIcon, Save, RefreshCw, Sliders, Check, Plus, Edit, Trash2, X, Tag, Truck, MapPin, MousePointer2, Printer, CreditCard, FileText, Eye, EyeOff, CheckCircle2, AlertCircle } from "lucide-react";
import { db } from "../firebase/config";
import { collection, query, where, getDocs, updateDoc, doc, addDoc, deleteDoc, onSnapshot, setDoc, getDoc } from "firebase/firestore";
import FloorplanEditor from "../components/floorplan/FloorplanEditor";
import AgreementTemplatesSettings from "../components/settings/AgreementTemplatesSettings";
import { EXCEL_PRODUCT_TAGS } from "../utils/excelTags";
import { logSecurityEvent } from "../services/AuditLogger";

export default function Settings() {
  const { currentUser } = useAuth();
  
  // Settings states
  const [markupRate, setMarkupRate] = useState(15);
  const [defaultTaxRate, setDefaultTaxRate] = useState(0);
  const [accentColor, setAccentColor] = useState("default");
  const [successMsg, setSuccessMsg] = useState("");
  const [activeTab, setActiveTab] = useState("general");
  const [dueDateDays, setDueDateDays] = useState(3);
  const [delayedShippingInterval, setDelayedShippingInterval] = useState(0);
  const [bizDevSubTab, setBizDevSubTab] = useState("categories"); // "categories" or "agreements"
  const { allCompanyLogos } = useCompanyLogos();
  const { users } = useUsers();

  // Companies states
  // Add form states
  const [newCompanyName, setNewCompanyName] = useState("");
  const [newCompanyPrimaryColor, setNewCompanyPrimaryColor] = useState("#3498db");
  const [newCompanyAddress, setNewCompanyAddress] = useState("");
  const [newCompanyAddress2, setNewCompanyAddress2] = useState("");
  const [newCompanyCity, setNewCompanyCity] = useState("");
  const [newCompanyState, setNewCompanyState] = useState("");
  const [newCompanyZip, setNewCompanyZip] = useState("");
  const [newCompanyPhone, setNewCompanyPhone] = useState("");
  const [newCompanyEmail, setNewCompanyEmail] = useState("");
  const [newCompanyWebsite, setNewCompanyWebsite] = useState("");
  const [newCompanyQbId, setNewCompanyQbId] = useState("");
  const [newCompanyQbSecret, setNewCompanyQbSecret] = useState("");
  const [newCompanyQbCompanyId, setNewCompanyQbCompanyId] = useState("");
  const [newCompanyQbEnvironment, setNewCompanyQbEnvironment] = useState("sandbox");
  const [showNewQbSecret, setShowNewQbSecret] = useState(false);
  const [newCompanyLogoFile, setNewCompanyLogoFile] = useState(null);
  const [newCompanyAssignedUsers, setNewCompanyAssignedUsers] = useState([]);
  const [newCompanyIsActive, setNewCompanyIsActive] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  // Edit form states
  const [editingCompany, setEditingCompany] = useState(null); // original logo/company object
  const [editCompanyName, setEditCompanyName] = useState("");
  const [editCompanyPrimaryColor, setEditCompanyPrimaryColor] = useState("#3498db");
  const [editCompanyAddress, setEditCompanyAddress] = useState("");
  const [editCompanyAddress2, setEditCompanyAddress2] = useState("");
  const [editCompanyCity, setEditCompanyCity] = useState("");
  const [editCompanyState, setEditCompanyState] = useState("");
  const [editCompanyZip, setEditCompanyZip] = useState("");
  const [editCompanyPhone, setEditCompanyPhone] = useState("");
  const [editCompanyEmail, setEditCompanyEmail] = useState("");
  const [editCompanyWebsite, setEditCompanyWebsite] = useState("");
  const [editCompanyQbId, setEditCompanyQbId] = useState("");
  const [editCompanyQbSecret, setEditCompanyQbSecret] = useState("");
  const [editCompanyQbCompanyId, setEditCompanyQbCompanyId] = useState("");
  const [editCompanyQbEnvironment, setEditCompanyQbEnvironment] = useState("sandbox");
  const [showEditQbSecret, setShowEditQbSecret] = useState(false);
  const [verifyingQb, setVerifyingQb] = useState(false);
  const [qbVerifyResult, setQbVerifyResult] = useState(null);
  const [editCompanyLogoFile, setEditCompanyLogoFile] = useState(null);
  const [editCompanyAssignedUsers, setEditCompanyAssignedUsers] = useState([]);
  const [editCompanyIsActive, setEditCompanyIsActive] = useState(false);
  
  // Invoicing states
  const [nonCcDiscount, setNonCcDiscount] = useState(3);
  const [invoicingSuccess, setInvoicingSuccess] = useState("");
  const [invoicingError, setInvoicingError] = useState("");

  // Vendor Categories states
  const [categories, setCategories] = useState([]);
  const [newCatName, setNewCatName] = useState("");
  const [editingCatId, setEditingCatId] = useState(null);
  const [editingCatName, setEditingCatName] = useState("");
  const [catError, setCatError] = useState("");
  const [catSuccess, setCatSuccess] = useState("");

  // Pay Schedules states
  const [paySchedules, setPaySchedules] = useState([]);
  const [newPayScheduleName, setNewPayScheduleName] = useState("");
  const [editingPayScheduleId, setEditingPayScheduleId] = useState(null);
  const [editingPayScheduleName, setEditingPayScheduleName] = useState("");
  const [payScheduleError, setPayScheduleError] = useState("");
  const [payScheduleSuccess, setPayScheduleSuccess] = useState("");

  // Product Tags states
  const [tags, setTags] = useState([]);
  const [newTagName, setNewTagName] = useState("");
  const [editingTagId, setEditingTagId] = useState(null);
  const [editingTagName, setEditingTagName] = useState("");
  const [tagError, setTagError] = useState("");
  const [tagSuccess, setTagSuccess] = useState("");
  const [showTagSuggestions, setShowTagSuggestions] = useState(false);

  // Brand Tags states
  const [brandTags, setBrandTags] = useState([]);
  const [newBrandTagName, setNewBrandTagName] = useState("");
  const [editingBrandTagId, setEditingBrandTagId] = useState(null);
  const [editingBrandTagName, setEditingBrandTagName] = useState("");
  const [brandTagError, setBrandTagError] = useState("");
  const [brandTagSuccess, setBrandTagSuccess] = useState("");

  // Canned Messages states
  const [cannedMessages, setCannedMessages] = useState([]);
  const [cannedName, setCannedName] = useState("");
  const [cannedContent, setCannedContent] = useState("");
  const [editingCannedId, setEditingCannedId] = useState(null);

  // Partner Categories states
  const [partnerCategories, setPartnerCategories] = useState([]);
  const [newPartnerCatName, setNewPartnerCatName] = useState("");
  const [editingPartnerCatId, setEditingPartnerCatId] = useState(null);
  const [editingPartnerCatName, setEditingPartnerCatName] = useState("");
  const [partnerCatError, setPartnerCatError] = useState("");
  const [partnerCatSuccess, setPartnerCatSuccess] = useState("");

  // Customer Payment Methods states (Procurement / Estimates / CRM Customers)
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [newPayMethodName, setNewPayMethodName] = useState("");
  const [editingPayMethodId, setEditingPayMethodId] = useState(null);
  const [editingPayMethodName, setEditingPayMethodName] = useState("");
  const [payMethodError, setPayMethodError] = useState("");
  const [payMethodSuccess, setPayMethodSuccess] = useState("");

  // Internal Payment Methods states (Purchase / Internal)
  const [internalPaymentMethods, setInternalPaymentMethods] = useState([]);
  const [newInternalPayMethodName, setNewInternalPayMethodName] = useState("");
  const [editingInternalPayMethodId, setEditingInternalPayMethodId] = useState(null);
  const [editingInternalPayMethodName, setEditingInternalPayMethodName] = useState("");
  const [internalPayMethodError, setInternalPayMethodError] = useState("");
  const [internalPayMethodSuccess, setInternalPayMethodSuccess] = useState("");

  // Carriers states
  const [carriersList, setCarriersList] = useState([]);
  const [newCarrierName, setNewCarrierName] = useState("");
  const [editingCarrierId, setEditingCarrierId] = useState(null);
  const [editingCarrierName, setEditingCarrierName] = useState("");
  // Warehouse Zones states
  const { zones, loading: zonesLoading, addZone, updateZone, deleteZone } = useZones();
  const { buildings, loading: buildingsLoading, addBuilding, updateBuilding, deleteBuilding } = useBuildings();
  const [selectedBuildingId, setSelectedBuildingId] = useState("");
  const [newZoneName, setNewZoneName] = useState("");
  const [editingZoneId, setEditingZoneId] = useState(null);
  const [editingZoneName, setEditingZoneName] = useState("");
  const [zoneError, setZoneError] = useState("");
  const [zoneSuccess, setZoneSuccess] = useState("");

  useEffect(() => {
    if (buildings.length > 0 && !selectedBuildingId) {
      const defaultB = buildings.find(b => b.name === "Main Warehouse") || buildings[0];
      setSelectedBuildingId(defaultB.id);
    }
  }, [buildings, selectedBuildingId]);

  const defaultBuilding = buildings.find(b => b.name === "Main Warehouse") || (buildings.length > 0 ? buildings[0] : null);
  
  const filteredZones = zones.filter(z => 
    z.buildingId === selectedBuildingId || 
    (!z.buildingId && defaultBuilding && selectedBuildingId === defaultBuilding.id)
  );

  async function handleAddZone(e) {
    e.preventDefault();
    setZoneError("");
    setZoneSuccess("");
    const name = newZoneName.trim();
    if (!name) return;
    if (zones.some(z => z.name.toLowerCase() === name.toLowerCase())) {
      setZoneError("Zone already exists.");
      return;
    }
    try {
      await addZone({ name });
      setNewZoneName("");
      setZoneSuccess("Zone added successfully!");
      setTimeout(() => setZoneSuccess(""), 3000);
    } catch (err) {
      setZoneError("Failed to add zone.");
    }
  }

  async function handleEditZone(zoneId, oldName) {
    setZoneError("");
    setZoneSuccess("");
    const newName = editingZoneName.trim();
    if (!newName) return;
    if (newName.toLowerCase() === oldName.toLowerCase()) {
      setEditingZoneId(null);
      return;
    }
    if (zones.some(z => z.id !== zoneId && z.name.toLowerCase() === newName.toLowerCase())) {
      setZoneError("Zone name already exists.");
      return;
    }
    try {
      await updateZone(zoneId, { name: newName });
      setEditingZoneId(null);
      setZoneSuccess("Zone updated!");
      setTimeout(() => setZoneSuccess(""), 3000);
    } catch (err) {
      setZoneError("Failed to update zone.");
    }
  }

  async function handleEditZoneProperties(zoneId, properties) {
    try {
      await updateZone(zoneId, properties);
      setZoneSuccess("Zone properties updated!");
      setTimeout(() => setZoneSuccess(""), 3000);
    } catch (err) {
      setZoneError("Failed to update zone properties.");
    }
  }

  async function handleDeleteZone(zoneId, name) {
    if (!window.confirm(`Are you sure you want to delete "${name}"?`)) return;
    setZoneError("");
    setZoneSuccess("");
    try {
      await deleteZone(zoneId);
      setZoneSuccess("Zone removed successfully!");
      setTimeout(() => setZoneSuccess(""), 3000);
    } catch (err) {
      setZoneError("Failed to delete zone.");
    }
  }

  async function handleClearAllZones() {
    if (!window.confirm("Are you sure you want to wipe ALL zones and layouts from the selected building floorplan? This cannot be undone.")) return;
    setZoneError("");
    setZoneSuccess("");
    try {
      for (const zone of filteredZones) {
        await deleteZone(zone.id);
      }
      setZoneSuccess("All zones cleared for this building.");
      setTimeout(() => setZoneSuccess(""), 3000);
    } catch (err) {
      setZoneError("Failed to clear zones.");
    }
  }

  const [carrierError, setCarrierError] = useState("");
  const [carrierSuccess, setCarrierSuccess] = useState("");

  // Facility Address states
  const [facilityAddress1, setFacilityAddress1] = useState("");
  const [facilityAddress2, setFacilityAddress2] = useState("");
  const [facilityCity, setFacilityCity] = useState("");
  const [facilityState, setFacilityState] = useState("");
  const [facilityZip, setFacilityZip] = useState("");
  const [facilityCountry, setFacilityCountry] = useState("");
  const [addressSuccess, setAddressSuccess] = useState("");
  const [addressError, setAddressError] = useState("");

  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, "vendor_categories"), async (snapshot) => {
      let list = [];
      snapshot.forEach((doc) => {
        list.push({ id: doc.id, ...doc.data() });
      });

      if (list.length === 0) {
        // Seed default categories
        const defaultCats = ["Logistics", "Procurement", "Warehousing", "Manufacturing", "Raw Materials", "Customs"];
        const seededList = [];
        for (const cat of defaultCats) {
          const docRef = await addDoc(collection(db, "vendor_categories"), { name: cat });
          seededList.push({ id: docRef.id, name: cat });
        }
        setCategories(seededList);
      } else {
        list.sort((a, b) => a.name.localeCompare(b.name));
        setCategories(list);
      }
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, "pay_schedules"), async (snapshot) => {
      let list = [];
      snapshot.forEach((doc) => {
        list.push({ id: doc.id, ...doc.data() });
      });

      if (list.length === 0) {
        // Seed default pay schedules
        const defaultSchedules = ["Net 15", "Net 30", "Weekly", "Bi-Weekly", "Monthly"];
        const seededList = [];
        for (const sched of defaultSchedules) {
          const docRef = await addDoc(collection(db, "pay_schedules"), { name: sched });
          seededList.push({ id: docRef.id, name: sched });
        }
        setPaySchedules(seededList);
      } else {
        list.sort((a, b) => a.name.localeCompare(b.name));
        setPaySchedules(list);
      }
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, "partner_categories"), async (snapshot) => {
      let list = [];
      snapshot.forEach((doc) => {
        list.push({ id: doc.id, ...doc.data() });
      });

      if (list.length === 0) {
        // Seed default partner categories
        const defaultCats = ["Subcontractor", "Teaming Partner", "Consultant", "Joint Venture", "Vendor", "Client"];
        const seededList = [];
        for (const cat of defaultCats) {
          const docRef = await addDoc(collection(db, "partner_categories"), { name: cat });
          seededList.push({ id: docRef.id, name: cat });
        }
        setPartnerCategories(seededList);
      } else {
        list.sort((a, b) => a.name.localeCompare(b.name));
        setPartnerCategories(list);
      }
    });

    return () => unsubscribe();
  }, []);

  async function handleAddPaySchedule(e) {
    e.preventDefault();
    setPayScheduleError("");
    setPayScheduleSuccess("");
    const name = newPayScheduleName.trim();
    if (!name) return;

    if (paySchedules.some(p => p.name.toLowerCase() === name.toLowerCase())) {
      setPayScheduleError("Pay Schedule already exists.");
      return;
    }

    try {
      await addDoc(collection(db, "pay_schedules"), { name });
      setNewPayScheduleName("");
      setPayScheduleSuccess("Pay Schedule added successfully!");
      setTimeout(() => setPayScheduleSuccess(""), 3000);
    } catch (err) {
      console.error("Error adding pay schedule:", err);
      setPayScheduleError("Failed to add pay schedule.");
    }
  }

  async function handleEditPaySchedule(schedId, oldName) {
    setPayScheduleError("");
    setPayScheduleSuccess("");
    const newName = editingPayScheduleName.trim();
    if (!newName) return;

    if (newName.toLowerCase() === oldName.toLowerCase()) {
      setEditingPayScheduleId(null);
      return;
    }

    if (paySchedules.some(p => p.id !== schedId && p.name.toLowerCase() === newName.toLowerCase())) {
      setPayScheduleError("Pay Schedule name already exists.");
      return;
    }

    try {
      await updateDoc(doc(db, "pay_schedules", schedId), { name: newName });
      
      // Cascade update to vendors
      const vQuery = query(collection(db, "vendors"), where("paySchedule", "==", oldName));
      const vSnap = await getDocs(vQuery);
      for (const vDoc of vSnap.docs) {
        await updateDoc(doc(db, "vendors", vDoc.id), { paySchedule: newName });
      }

      // Cascade update to customers
      const cQuery = query(collection(db, "customers"), where("paySchedule", "==", oldName));
      const cSnap = await getDocs(cQuery);
      for (const cDoc of cSnap.docs) {
        await updateDoc(doc(db, "customers", cDoc.id), { paySchedule: newName });
      }

      setEditingPayScheduleId(null);
      setPayScheduleSuccess("Pay Schedule updated and synced successfully!");
      setTimeout(() => setPayScheduleSuccess(""), 3000);
    } catch (err) {
      console.error("Error updating pay schedule:", err);
      setPayScheduleError("Failed to update pay schedule.");
    }
  }

  async function handleDeletePaySchedule(schedId, name) {
    if (!window.confirm(`Are you sure you want to delete "${name}"? Existing customers and vendors with this pay schedule will be cleared.`)) {
      return;
    }

    setPayScheduleError("");
    setPayScheduleSuccess("");

    try {
      await deleteDoc(doc(db, "pay_schedules", schedId));

      // Cascade clear to vendors
      const vQuery = query(collection(db, "vendors"), where("paySchedule", "==", name));
      const vSnap = await getDocs(vQuery);
      for (const vDoc of vSnap.docs) {
        await updateDoc(doc(db, "vendors", vDoc.id), { paySchedule: "" });
      }

      // Cascade clear to customers
      const cQuery = query(collection(db, "customers"), where("paySchedule", "==", name));
      const cSnap = await getDocs(cQuery);
      for (const cDoc of cSnap.docs) {
        await updateDoc(doc(db, "customers", cDoc.id), { paySchedule: "" });
      }

      setPayScheduleSuccess("Pay Schedule removed successfully!");
      setTimeout(() => setPayScheduleSuccess(""), 3000);
    } catch (err) {
      console.error("Error deleting pay schedule:", err);
      setPayScheduleError("Failed to delete pay schedule.");
    }
  }

  async function handleAddPartnerCategory(e) {
    e.preventDefault();
    setPartnerCatError("");
    setPartnerCatSuccess("");
    const name = newPartnerCatName.trim();
    if (!name) return;

    if (partnerCategories.some(c => c.name.toLowerCase() === name.toLowerCase())) {
      setPartnerCatError("Category already exists.");
      return;
    }

    try {
      await addDoc(collection(db, "partner_categories"), { name });
      setNewPartnerCatName("");
      setPartnerCatSuccess("Category added successfully!");
      setTimeout(() => setPartnerCatSuccess(""), 3000);
    } catch (err) {
      console.error("Error adding partner category:", err);
      setPartnerCatError("Failed to add category.");
    }
  }

  async function handleEditPartnerCategory(catId, oldName) {
    setPartnerCatError("");
    setPartnerCatSuccess("");
    const newName = editingPartnerCatName.trim();
    if (!newName) return;

    if (newName.toLowerCase() === oldName.toLowerCase()) {
      setEditingPartnerCatId(null);
      return;
    }

    if (partnerCategories.some(c => c.id !== catId && c.name.toLowerCase() === newName.toLowerCase())) {
      setPartnerCatError("Category name already exists.");
      return;
    }

    try {
      await updateDoc(doc(db, "partner_categories", catId), { name: newName });
      
      // Cascade update to partners
      const q = query(collection(db, "partners"), where("categoryName", "==", oldName));
      const querySnapshot = await getDocs(q);
      for (const partnerDoc of querySnapshot.docs) {
        await updateDoc(doc(db, "partners", partnerDoc.id), { categoryName: newName });
      }

      setEditingPartnerCatId(null);
      setPartnerCatSuccess("Category updated successfully!");
      setTimeout(() => setPartnerCatSuccess(""), 3000);
    } catch (err) {
      console.error("Error editing partner category:", err);
      setPartnerCatError("Failed to edit category.");
    }
  }

  async function handleDeletePartnerCategory(catId, catName) {
    if (!window.confirm(`Are you sure you want to delete category "${catName}"?`)) return;
    setPartnerCatError("");
    setPartnerCatSuccess("");

    try {
      await deleteDoc(doc(db, "partner_categories", catId));
      setPartnerCatSuccess("Category deleted successfully!");
      setTimeout(() => setPartnerCatSuccess(""), 3000);
    } catch (err) {
      console.error("Error deleting partner category:", err);
      setPartnerCatError("Failed to delete category.");
    }
  }

  async function handleAddCategory(e) {
    e.preventDefault();
    setCatError("");
    setCatSuccess("");
    const name = newCatName.trim();
    if (!name) return;

    if (categories.some(c => c.name.toLowerCase() === name.toLowerCase())) {
      setCatError("Category already exists.");
      return;
    }

    try {
      await addDoc(collection(db, "vendor_categories"), { name });
      setNewCatName("");
      setCatSuccess("Category added successfully!");
      setTimeout(() => setCatSuccess(""), 3000);
    } catch (err) {
      console.error("Error adding category:", err);
      setCatError("Failed to add category.");
    }
  }

  async function handleEditCategory(catId, oldName) {
    setCatError("");
    setCatSuccess("");
    const newName = editingCatName.trim();
    if (!newName) return;

    if (newName.toLowerCase() === oldName.toLowerCase()) {
      setEditingCatId(null);
      return;
    }

    if (categories.some(c => c.id !== catId && c.name.toLowerCase() === newName.toLowerCase())) {
      setCatError("Category name already exists.");
      return;
    }

    try {
      await updateDoc(doc(db, "vendor_categories", catId), { name: newName });
      
      // Cascade update to vendors
      const q = query(collection(db, "vendors"), where("category", "==", oldName));
      const querySnapshot = await getDocs(q);
      for (const vendorDoc of querySnapshot.docs) {
        await updateDoc(doc(db, "vendors", vendorDoc.id), { category: newName });
      }

      setEditingCatId(null);
      setCatSuccess("Category updated and synced with vendors!");
      setTimeout(() => setCatSuccess(""), 3000);
    } catch (err) {
      console.error("Error updating category:", err);
      setCatError("Failed to update category.");
    }
  }

  async function handleDeleteCategory(catId, name) {
    if (!window.confirm(`Are you sure you want to delete "${name}"? Existing vendors in this category will be marked as "Uncategorized".`)) {
      return;
    }

    setCatError("");
    setCatSuccess("");

    try {
      await deleteDoc(doc(db, "vendor_categories", catId));

      // Cascade update to vendors
      const q = query(collection(db, "vendors"), where("category", "==", name));
      const querySnapshot = await getDocs(q);
      for (const vendorDoc of querySnapshot.docs) {
        await updateDoc(doc(db, "vendors", vendorDoc.id), { category: "Uncategorized" });
      }

      setCatSuccess("Category removed successfully!");
      setTimeout(() => setCatSuccess(""), 3000);
    } catch (err) {
      console.error("Error deleting category:", err);
      setCatError("Failed to delete category.");
    }
  }

  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, "product_tags"), async (snapshot) => {
      let list = [];
      snapshot.forEach((doc) => {
        list.push({ id: doc.id, ...doc.data() });
      });

      if (list.length === 0) {
        // Seed default tags
        const defaultTags = ["Electronics", "Steel", "Packaging", "Fasteners", "Chemicals", "Hardware", "Services", "Antenna", "Tobacco"];
        const seededList = [];
        for (const tag of defaultTags) {
          const docRef = await addDoc(collection(db, "product_tags"), { name: tag });
          seededList.push({ id: docRef.id, name: tag });
        }
        setTags(seededList);
      } else {
        // Ensure Antenna and Tobacco are present
        const requiredTags = ["Antenna", "Tobacco"];
        for (const reqTag of requiredTags) {
          if (!list.some(t => t.name && t.name.toLowerCase() === reqTag.toLowerCase())) {
            addDoc(collection(db, "product_tags"), { name: reqTag }).catch(err => console.error("Error adding tag:", err));
          }
        }
        list.sort((a, b) => a.name.localeCompare(b.name));
        setTags(list);
      }
    });

    return () => unsubscribe();
  }, []);

  async function handleAddTag(e) {
    e.preventDefault();
    setTagError("");
    setTagSuccess("");
    const name = newTagName.trim();
    if (!name) return;

    if (tags.some(t => t.name.toLowerCase() === name.toLowerCase())) {
      setTagError("Tag already exists.");
      return;
    }

    try {
      await addDoc(collection(db, "product_tags"), { name });
      setNewTagName("");
      setTagSuccess("Tag added successfully!");
      setTimeout(() => setTagSuccess(""), 3000);
    } catch (err) {
      console.error("Error adding tag:", err);
      setTagError("Failed to add tag.");
    }
  }

  async function handleEditTag(tagId, oldName) {
    setTagError("");
    setTagSuccess("");
    const newName = editingTagName.trim();
    if (!newName) return;

    if (newName.toLowerCase() === oldName.toLowerCase()) {
      setEditingTagId(null);
      return;
    }

    if (tags.some(t => t.id !== tagId && t.name.toLowerCase() === newName.toLowerCase())) {
      setTagError("Tag name already exists.");
      return;
    }

    try {
      await updateDoc(doc(db, "product_tags", tagId), { name: newName });
      
      // Cascade update to vendors
      const q = query(collection(db, "vendors"), where("tags", "array-contains", oldName));
      const querySnapshot = await getDocs(q);
      for (const vendorDoc of querySnapshot.docs) {
        const vendorData = vendorDoc.data();
        const currentTags = vendorData.tags || [];
        const updatedTags = currentTags.map(t => t === oldName ? newName : t);
        await updateDoc(doc(db, "vendors", vendorDoc.id), { tags: updatedTags });
      }

      setEditingTagId(null);
      setTagSuccess("Tag updated and synced with vendors!");
      setTimeout(() => setTagSuccess(""), 3000);
    } catch (err) {
      console.error("Error updating tag:", err);
      setTagError("Failed to update tag.");
    }
  }

  async function handleDeleteTag(tagId, name) {
    if (!window.confirm(`Are you sure you want to delete "${name}"? It will be removed from all vendors.`)) {
      return;
    }

    setTagError("");
    setTagSuccess("");

    try {
      await deleteDoc(doc(db, "product_tags", tagId));

      // Cascade update to vendors
      const q = query(collection(db, "vendors"), where("tags", "array-contains", name));
      const querySnapshot = await getDocs(q);
      for (const vendorDoc of querySnapshot.docs) {
        const vendorData = vendorDoc.data();
        const currentTags = vendorData.tags || [];
        const updatedTags = currentTags.filter(t => t !== name);
        await updateDoc(doc(db, "vendors", vendorDoc.id), { tags: updatedTags });
      }

      setTagSuccess("Tag removed successfully!");
      setTimeout(() => setTagSuccess(""), 3000);
    } catch (err) {
      console.error("Error deleting tag:", err);
      setTagError("Failed to delete tag.");
    }
  }

  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, "brand_tags"), async (snapshot) => {
      let list = [];
      snapshot.forEach((doc) => {
        list.push({ id: doc.id, ...doc.data() });
      });

      if (list.length === 0) {
        // Seed default brand tags
        const defaultBrands = ["Fastenal", "Grainger", "MSC Industrial", "McMaster-Carr", "3M", "Schneider Electric", "Rockwell Automation"];
        const seededList = [];
        for (const brand of defaultBrands) {
          const docRef = await addDoc(collection(db, "brand_tags"), { name: brand });
          seededList.push({ id: docRef.id, name: brand });
        }
        setBrandTags(seededList);
      } else {
        list.sort((a, b) => a.name.localeCompare(b.name));
        setBrandTags(list);
      }
    });

    return () => unsubscribe();
  }, []);

  async function handleAddBrandTag(e) {
    e.preventDefault();
    setBrandTagError("");
    setBrandTagSuccess("");
    const name = newBrandTagName.trim();
    if (!name) return;

    if (brandTags.some(t => t.name.toLowerCase() === name.toLowerCase())) {
      setBrandTagError("Brand Tag already exists.");
      return;
    }

    try {
      await addDoc(collection(db, "brand_tags"), { name });
      setNewBrandTagName("");
      setBrandTagSuccess("Brand Tag added successfully!");
      setTimeout(() => setBrandTagSuccess(""), 3000);
    } catch (err) {
      console.error("Error adding brand tag:", err);
      setBrandTagError("Failed to add brand tag.");
    }
  }

  async function handleEditBrandTag(tagId, oldName) {
    setBrandTagError("");
    setBrandTagSuccess("");
    const newName = editingBrandTagName.trim();
    if (!newName) return;

    if (newName.toLowerCase() === oldName.toLowerCase()) {
      setEditingBrandTagId(null);
      return;
    }

    if (brandTags.some(t => t.id !== tagId && t.name.toLowerCase() === newName.toLowerCase())) {
      setBrandTagError("Brand Tag name already exists.");
      return;
    }

    try {
      await updateDoc(doc(db, "brand_tags", tagId), { name: newName });
      
      // Cascade update to vendors
      const q = query(collection(db, "vendors"), where("brandTags", "array-contains", oldName));
      const querySnapshot = await getDocs(q);
      for (const vendorDoc of querySnapshot.docs) {
        const vendorData = vendorDoc.data();
        const currentTags = vendorData.brandTags || [];
        const updatedTags = currentTags.map(t => t === oldName ? newName : t);
        await updateDoc(doc(db, "vendors", vendorDoc.id), { brandTags: updatedTags });
      }

      setEditingBrandTagId(null);
      setBrandTagSuccess("Brand Tag updated and synced with vendors!");
      setTimeout(() => setBrandTagSuccess(""), 3000);
    } catch (err) {
      console.error("Error updating brand tag:", err);
      setBrandTagError("Failed to update brand tag.");
    }
  }

  async function handleDeleteBrandTag(tagId, name) {
    if (!window.confirm(`Are you sure you want to delete brand tag "${name}"? It will be removed from all vendors.`)) {
      return;
    }

    setBrandTagError("");
    setBrandTagSuccess("");

    try {
      await deleteDoc(doc(db, "brand_tags", tagId));

      // Cascade update to vendors
      const q = query(collection(db, "vendors"), where("brandTags", "array-contains", name));
      const querySnapshot = await getDocs(q);
      for (const vendorDoc of querySnapshot.docs) {
        const vendorData = vendorDoc.data();
        const currentTags = vendorData.brandTags || [];
        const updatedTags = currentTags.filter(t => t !== name);
        await updateDoc(doc(db, "vendors", vendorDoc.id), { brandTags: updatedTags });
      }

      setBrandTagSuccess("Brand Tag removed successfully!");
      setTimeout(() => setBrandTagSuccess(""), 3000);
    } catch (err) {
      console.error("Error deleting brand tag:", err);
      setBrandTagError("Failed to delete brand tag.");
    }
  }

  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, "payment_methods"), async (snapshot) => {
      let list = [];
      snapshot.forEach((doc) => {
        list.push({ id: doc.id, ...doc.data() });
      });

      if (list.length === 0) {
        // Seed default payment methods
        const defaultMethods = ["Credit Card", "ACH", "Wire Transfer", "Check", "Net 30"];
        const seededList = [];
        for (const method of defaultMethods) {
          const docRef = await addDoc(collection(db, "payment_methods"), { name: method });
          seededList.push({ id: docRef.id, name: method });
        }
        setPaymentMethods(seededList);
      } else {
        list.sort((a, b) => a.name.localeCompare(b.name));
        setPaymentMethods(list);
      }
    });

    return () => unsubscribe();
  }, []);

  async function handleAddPaymentMethod(e) {
    e.preventDefault();
    setPayMethodError("");
    setPayMethodSuccess("");
    const name = newPayMethodName.trim();
    if (!name) return;

    if (paymentMethods.some(pm => pm.name.toLowerCase() === name.toLowerCase())) {
      setPayMethodError("Payment Method already exists.");
      return;
    }

    try {
      await addDoc(collection(db, "payment_methods"), { name });
      setNewPayMethodName("");
      setPayMethodSuccess("Payment Method added successfully!");
      setTimeout(() => setPayMethodSuccess(""), 3000);
    } catch (err) {
      console.error("Error adding payment method:", err);
      setPayMethodError("Failed to add payment method.");
    }
  }

  async function handleEditPaymentMethod(pmId, oldName) {
    setPayMethodError("");
    setPayMethodSuccess("");
    const newName = editingPayMethodName.trim();
    if (!newName) return;

    if (newName.toLowerCase() === oldName.toLowerCase()) {
      setEditingPayMethodId(null);
      return;
    }

    if (paymentMethods.some(pm => pm.id !== pmId && pm.name.toLowerCase() === newName.toLowerCase())) {
      setPayMethodError("Payment Method name already exists.");
      return;
    }

    try {
      await updateDoc(doc(db, "payment_methods", pmId), { name: newName });
      
      // Cascade update to customers
      const q = query(collection(db, "customers"), where("preferredPaymentMethod", "==", oldName));
      const querySnapshot = await getDocs(q);
      for (const customerDoc of querySnapshot.docs) {
        await updateDoc(doc(db, "customers", customerDoc.id), { preferredPaymentMethod: newName });
      }

      setEditingPayMethodId(null);
      setPayMethodSuccess("Payment Method updated and synced with customers!");
      setTimeout(() => setPayMethodSuccess(""), 3000);
    } catch (err) {
      console.error("Error updating payment method:", err);
      setPayMethodError("Failed to update payment method.");
    }
  }

  async function handleDeletePaymentMethod(pmId, name) {
    if (!window.confirm(`Are you sure you want to delete "${name}"? Existing customers with this preferred payment method will have it cleared.`)) {
      return;
    }

    setPayMethodError("");
    setPayMethodSuccess("");

    try {
      await deleteDoc(doc(db, "payment_methods", pmId));

      // Cascade update to customers
      const q = query(collection(db, "customers"), where("preferredPaymentMethod", "==", name));
      const querySnapshot = await getDocs(q);
      for (const customerDoc of querySnapshot.docs) {
        await updateDoc(doc(db, "customers", customerDoc.id), { preferredPaymentMethod: "" });
      }

      setPayMethodSuccess("Payment Method removed successfully!");
      setTimeout(() => setPayMethodSuccess(""), 3000);
    } catch (err) {
      console.error("Error deleting payment method:", err);
      setPayMethodError("Failed to delete payment method.");
    }
  }

  // Internal Payment Methods (Purchase) Listener & Handlers
  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, "internal_payment_methods"), async (snapshot) => {
      let list = [];
      snapshot.forEach((doc) => {
        list.push({ id: doc.id, ...doc.data() });
      });

      if (list.length === 0) {
        // Seed default internal payment methods
        const defaultMethods = ["Company Credit Card", "Corporate Amex", "Bank Wire", "Company Check", "ACH"];
        const seededList = [];
        for (const method of defaultMethods) {
          const docRef = await addDoc(collection(db, "internal_payment_methods"), { name: method });
          seededList.push({ id: docRef.id, name: method });
        }
        setInternalPaymentMethods(seededList);
      } else {
        list.sort((a, b) => a.name.localeCompare(b.name));
        setInternalPaymentMethods(list);
      }
    });

    return () => unsubscribe();
  }, []);

  async function handleAddInternalPaymentMethod(e) {
    e.preventDefault();
    setInternalPayMethodError("");
    setInternalPayMethodSuccess("");
    const name = newInternalPayMethodName.trim();
    if (!name) return;

    if (internalPaymentMethods.some(pm => pm.name.toLowerCase() === name.toLowerCase())) {
      setInternalPayMethodError("Internal Payment Method already exists.");
      return;
    }

    try {
      await addDoc(collection(db, "internal_payment_methods"), { name });
      setNewInternalPayMethodName("");
      setInternalPayMethodSuccess("Internal Payment Method added successfully!");
      setTimeout(() => setInternalPayMethodSuccess(""), 3000);
    } catch (err) {
      console.error("Error adding internal payment method:", err);
      setInternalPayMethodError("Failed to add internal payment method.");
    }
  }

  async function handleEditInternalPaymentMethod(pmId, oldName) {
    setInternalPayMethodError("");
    setInternalPayMethodSuccess("");
    const newName = editingInternalPayMethodName.trim();
    if (!newName) return;

    if (newName.toLowerCase() === oldName.toLowerCase()) {
      setEditingInternalPayMethodId(null);
      return;
    }

    if (internalPaymentMethods.some(pm => pm.id !== pmId && pm.name.toLowerCase() === newName.toLowerCase())) {
      setInternalPayMethodError("Internal Payment Method name already exists.");
      return;
    }

    try {
      await updateDoc(doc(db, "internal_payment_methods", pmId), { name: newName });
      setEditingInternalPayMethodId(null);
      setInternalPayMethodSuccess("Internal Payment Method updated successfully!");
      setTimeout(() => setInternalPayMethodSuccess(""), 3000);
    } catch (err) {
      console.error("Error updating internal payment method:", err);
      setInternalPayMethodError("Failed to update internal payment method.");
    }
  }

  async function handleDeleteInternalPaymentMethod(pmId, name) {
    if (!window.confirm(`Are you sure you want to delete "${name}"?`)) {
      return;
    }

    setInternalPayMethodError("");
    setInternalPayMethodSuccess("");

    try {
      await deleteDoc(doc(db, "internal_payment_methods", pmId));
      setInternalPayMethodSuccess("Internal Payment Method removed successfully!");
      setTimeout(() => setInternalPayMethodSuccess(""), 3000);
    } catch (err) {
      console.error("Error deleting internal payment method:", err);
      setInternalPayMethodError("Failed to delete internal payment method.");
    }
  }

  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, "carriers"), async (snapshot) => {
      let list = [];
      snapshot.forEach((doc) => {
        list.push({ id: doc.id, ...doc.data() });
      });

      if (list.length === 0) {
        const defaultCarriers = ["DHL Express", "FedEx Supply Chain", "Atlas Freight Lines", "Maersk Ocean Cargo", "UPS Logistics"];
        const seededList = [];
        for (const carrier of defaultCarriers) {
          const docRef = await addDoc(collection(db, "carriers"), { name: carrier });
          seededList.push({ id: docRef.id, name: carrier });
        }
        setCarriersList(seededList);
      } else {
        list.sort((a, b) => a.name.localeCompare(b.name));
        setCarriersList(list);
      }
    });

    return () => unsubscribe();
  }, []);

  async function handleAddCarrier(e) {
    e.preventDefault();
    setCarrierError("");
    setCarrierSuccess("");
    const name = newCarrierName.trim();
    if (!name) return;

    if (carriersList.some(c => c.name.toLowerCase() === name.toLowerCase())) {
      setCarrierError("Carrier already exists.");
      return;
    }

    try {
      await addDoc(collection(db, "carriers"), { name });
      setNewCarrierName("");
      setCarrierSuccess("Carrier added successfully!");
      setTimeout(() => setCarrierSuccess(""), 3000);
    } catch (err) {
      console.error("Error adding carrier:", err);
      setCarrierError("Failed to add carrier.");
    }
  }

  async function handleEditCarrier(carrierId, oldName) {
    setCarrierError("");
    setCarrierSuccess("");
    const newName = editingCarrierName.trim();
    if (!newName) return;

    if (newName.toLowerCase() === oldName.toLowerCase()) {
      setEditingCarrierId(null);
      return;
    }

    if (carriersList.some(c => c.id !== carrierId && c.name.toLowerCase() === newName.toLowerCase())) {
      setCarrierError("Carrier name already exists.");
      return;
    }

    try {
      await updateDoc(doc(db, "carriers", carrierId), { name: newName });
      
      const q = query(collection(db, "logistics"), where("carrier", "==", oldName));
      const querySnapshot = await getDocs(q);
      for (const logDoc of querySnapshot.docs) {
        await updateDoc(doc(db, "logistics", logDoc.id), { carrier: newName });
      }

      setEditingCarrierId(null);
      setCarrierSuccess("Carrier updated and synced with shipments!");
      setTimeout(() => setCarrierSuccess(""), 3000);
    } catch (err) {
      console.error("Error updating carrier:", err);
      setCarrierError("Failed to update carrier.");
    }
  }

  async function handleDeleteCarrier(carrierId, name) {
    if (!window.confirm(`Are you sure you want to delete "${name}"? It will set matching shipments to "Pending Carrier".`)) {
      return;
    }

    setCarrierError("");
    setCarrierSuccess("");

    try {
      await deleteDoc(doc(db, "carriers", carrierId));

      const q = query(collection(db, "logistics"), where("carrier", "==", name));
      const querySnapshot = await getDocs(q);
      for (const logDoc of querySnapshot.docs) {
        await updateDoc(doc(db, "logistics", logDoc.id), { carrier: "Pending Carrier" });
      }

      setCarrierSuccess("Carrier removed and shipments updated.");
      setTimeout(() => setCarrierSuccess(""), 3000);
    } catch (err) {
      console.error("Error deleting carrier:", err);
      setCarrierError("Failed to delete carrier.");
    }
  }



  useEffect(() => {
    // Load markup rate
    const savedMarkup = localStorage.getItem("global_markup_rate");
    if (savedMarkup !== null) {
      setMarkupRate(parseFloat(savedMarkup));
    }

    // Load accent color
    const savedAccent = localStorage.getItem("global_accent_color");
    if (savedAccent) {
      setAccentColor(savedAccent);
    }

    // Load default due date days
    const savedDueDateDays = localStorage.getItem("global_po_due_date_days");
    if (savedDueDateDays !== null) {
      setDueDateDays(parseInt(savedDueDateDays, 10));
    }

    // Load delayed shipping interval
    const savedShippingInterval = localStorage.getItem("global_delayed_shipping_interval");
    if (savedShippingInterval !== null) {
      setDelayedShippingInterval(parseInt(savedShippingInterval, 10));
    }

    // Load non-cc discount percentage
    const savedNonCcDiscount = localStorage.getItem("global_non_cc_discount");
    if (savedNonCcDiscount !== null) {
      setNonCcDiscount(parseFloat(savedNonCcDiscount));
    } else {
      setNonCcDiscount(3);
    }

    // Load default tax rate
    const savedTaxRate = localStorage.getItem("global_default_tax_rate");
    if (savedTaxRate !== null && savedTaxRate !== "") {
      setDefaultTaxRate(parseFloat(savedTaxRate) || 0);
    }

    // Also fetch general settings from Firestore if available
    getDoc(doc(db, "settings", "general")).then((snap) => {
      if (snap.exists()) {
        const data = snap.data();
        if (data.defaultTaxRate !== undefined) {
          setDefaultTaxRate(Number(data.defaultTaxRate));
          localStorage.setItem("global_default_tax_rate", data.defaultTaxRate.toString());
        }
      }
    }).catch(err => {
      console.warn("Could not fetch general settings from Firestore:", err);
    });

    // Load facility address settings
    const savedAddress = localStorage.getItem("facility_address");
    if (savedAddress) {
      try {
        const addrObj = JSON.parse(savedAddress);
        if (addrObj.address1) setFacilityAddress1(addrObj.address1);
        if (addrObj.address2) setFacilityAddress2(addrObj.address2);
        if (addrObj.city) setFacilityCity(addrObj.city);
        if (addrObj.state) setFacilityState(addrObj.state);
        if (addrObj.zip) setFacilityZip(addrObj.zip);
        if (addrObj.country) setFacilityCountry(addrObj.country);
      } catch (e) {
        console.error("Failed to parse facility address:", e);
      }
    }
  }, []);

  const handleSaveGeneral = async (e) => {
    e.preventDefault();
    localStorage.setItem("global_accent_color", accentColor);
    localStorage.setItem("global_default_tax_rate", defaultTaxRate.toString());
    
    // Apply accent color variables system-wide
    if (accentColor === "purple") {
      document.documentElement.style.setProperty("--primary", "hsl(270, 85%, 60%)");
      document.documentElement.style.setProperty("--primary-hover", "hsl(270, 85%, 50%)");
      document.documentElement.style.setProperty("--border-focus", "hsla(270, 85%, 60%, 0.4)");
    } else if (accentColor === "emerald") {
      document.documentElement.style.setProperty("--primary", "hsl(150, 85%, 45%)");
      document.documentElement.style.setProperty("--primary-hover", "hsl(150, 85%, 35%)");
      document.documentElement.style.setProperty("--border-focus", "hsla(150, 85%, 45%, 0.4)");
    } else {
      // Default blue
      document.documentElement.style.setProperty("--primary", "hsl(215, 85%, 60%)");
      document.documentElement.style.setProperty("--primary-hover", "hsl(215, 85%, 50%)");
      document.documentElement.style.setProperty("--border-focus", "hsla(215, 85%, 60%, 0.4)");
    }

    try {
      await setDoc(doc(db, "settings", "general"), {
        defaultTaxRate: Number(defaultTaxRate) || 0,
        updatedAt: new Date().toISOString(),
        updatedBy: currentUser?.email || "anonymous"
      }, { merge: true });

      await logSecurityEvent({
        action: "SETTINGS_UPDATED",
        category: "CONFIGURATION",
        userId: currentUser?.uid || "anonymous",
        userEmail: currentUser?.email || "anonymous",
        resource: "settings/general",
        status: "SUCCESS",
        details: { defaultTaxRate: Number(defaultTaxRate) || 0 }
      });
    } catch (fsErr) {
      console.error("Failed to sync settings to Firestore:", fsErr);
    }

    setSuccessMsg("General settings saved successfully!");
    setTimeout(() => setSuccessMsg(""), 3000);
  };

  const handleLogoUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    setNewCompanyLogoFile(files[0]);
  };

  const handleEditLogoUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    setEditCompanyLogoFile(files[0]);
  };

  const handleCreateCompany = async (e) => {
    e.preventDefault();
    if (!newCompanyName.trim()) {
      window.alert("Company name is required.");
      return;
    }
    if (!newCompanyQbId.trim()) {
      window.alert("QB ID is required.");
      return;
    }
    if (!newCompanyQbSecret.trim()) {
      window.alert("QB Secret is required.");
      return;
    }
    if (!newCompanyQbCompanyId.trim()) {
      window.alert("QB Company ID is required.");
      return;
    }
    if (!newCompanyLogoFile) {
      window.alert("Please select a company logo file.");
      return;
    }
    try {
      setUploadingLogo(true);
      await uploadCompanyLogo(
        newCompanyLogoFile, 
        newCompanyName.trim(),
        newCompanyPrimaryColor,
        newCompanyAddress,
        newCompanyAddress2,
        newCompanyCity,
        newCompanyState,
        newCompanyZip,
        newCompanyAssignedUsers,
        newCompanyIsActive,
        newCompanyQbId.trim(),
        newCompanyQbSecret.trim(),
        newCompanyQbCompanyId.trim(),
        newCompanyPhone.trim(),
        newCompanyEmail.trim(),
        newCompanyWebsite.trim(),
        newCompanyQbEnvironment
      );
      setNewCompanyName("");
      setNewCompanyPrimaryColor("#3498db");
      setNewCompanyAddress("");
      setNewCompanyAddress2("");
      setNewCompanyCity("");
      setNewCompanyState("");
      setNewCompanyZip("");
      setNewCompanyPhone("");
      setNewCompanyEmail("");
      setNewCompanyWebsite("");
      setNewCompanyQbId("");
      setNewCompanyQbSecret("");
      setNewCompanyQbCompanyId("");
      setNewCompanyQbEnvironment("sandbox");
      setShowNewQbSecret(false);
      setNewCompanyLogoFile(null);
      setNewCompanyAssignedUsers([]);
      setNewCompanyIsActive(false);
      setSuccessMsg("Company added successfully!");
      setTimeout(() => setSuccessMsg(""), 3000);
      const fileInput = document.getElementById("company-logo-input");
      if (fileInput) fileInput.value = "";
    } catch (err) {
      console.error("Error creating company:", err);
      window.alert("Failed to create company.");
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleStartEdit = (logo) => {
    setEditingCompany(logo);
    setEditCompanyName(logo.name || "");
    setEditCompanyPrimaryColor(logo.primaryColor || "#3498db");
    setEditCompanyAddress(logo.address || "");
    setEditCompanyAddress2(logo.address2 || "");
    setEditCompanyCity(logo.city || "");
    setEditCompanyState(logo.state || "");
    setEditCompanyZip(logo.zip || "");
    setEditCompanyPhone(logo.phone || "");
    setEditCompanyEmail(logo.email || "");
    setEditCompanyWebsite(logo.website || "");
    setEditCompanyQbId(logo.qbId || "");
    setEditCompanyQbSecret(logo.qbSecret || "");
    setEditCompanyQbCompanyId(logo.qbCompanyId || "");
    setEditCompanyQbEnvironment(logo.qbEnvironment || "sandbox");
    setShowEditQbSecret(false);
    setQbVerifyResult(null);
    setEditCompanyLogoFile(null);
    setEditCompanyAssignedUsers(logo.assignedUsers || []);
    setEditCompanyIsActive(!!logo.isActive);
  };

  const handleVerifyQbConnection = async (e) => {
    if (e) e.preventDefault();
    if (!editingCompany) return;
    if (!editCompanyQbId.trim() || !editCompanyQbSecret.trim() || !editCompanyQbCompanyId.trim()) {
      setQbVerifyResult({
        success: false,
        message: "Please enter QB ID, QB Secret, and QB Company ID before verifying."
      });
      return;
    }

    try {
      setVerifyingQb(true);
      setQbVerifyResult(null);
      const res = await verifyCompanyQuickBooksConnection(editingCompany.docId, {
        qbId: editCompanyQbId,
        qbSecret: editCompanyQbSecret,
        qbCompanyId: editCompanyQbCompanyId
      });
      setQbVerifyResult(res);
      setEditingCompany(prev => ({ ...prev, ...res }));
    } catch (err) {
      console.error("Error verifying QuickBooks connection:", err);
      setQbVerifyResult({
        success: false,
        message: err.message || "Failed to verify QuickBooks connection."
      });
    } finally {
      setVerifyingQb(false);
    }
  };

  const handleUpdateCompany = async (e) => {
    e.preventDefault();
    if (!editingCompany) return;
    if (!editCompanyName.trim()) {
      window.alert("Company name is required.");
      return;
    }
    if (!editCompanyQbId.trim()) {
      window.alert("QB ID is required.");
      return;
    }
    if (!editCompanyQbSecret.trim()) {
      window.alert("QB Secret is required.");
      return;
    }
    if (!editCompanyQbCompanyId.trim()) {
      window.alert("QB Company ID is required.");
      return;
    }

    try {
      setUploadingLogo(true);
      const updatedData = {
        name: editCompanyName.trim(),
        primaryColor: editCompanyPrimaryColor,
        address: editCompanyAddress,
        address2: editCompanyAddress2,
        city: editCompanyCity,
        state: editCompanyState,
        zip: editCompanyZip,
        phone: editCompanyPhone.trim(),
        email: editCompanyEmail.trim(),
        website: editCompanyWebsite.trim(),
        assignedUsers: editCompanyAssignedUsers,
        qbId: editCompanyQbId.trim(),
        qbSecret: editCompanyQbSecret.trim(),
        qbCompanyId: editCompanyQbCompanyId.trim(),
        qbEnvironment: editCompanyQbEnvironment || "sandbox",
        qbVerified: editingCompany.qbVerified || true,
        qbVerifiedAt: editingCompany.qbVerifiedAt || new Date().toISOString(),
        qbStatus: "Connected",
        isActive: editCompanyIsActive,
        url: editingCompany.url,
        path: editingCompany.path
      };

      await updateCompany(editingCompany.docId, updatedData, editCompanyLogoFile);
      setEditingCompany(null);
      setSuccessMsg("Company updated successfully!");
      setTimeout(() => setSuccessMsg(""), 3000);
      const fileInput = document.getElementById("edit-company-logo-input");
      if (fileInput) fileInput.value = "";
    } catch (err) {
      console.error("Error updating company:", err);
      window.alert("Failed to update company.");
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleSetActiveLogo = async (logoId) => {
    try {
      await setActiveLogo(logoId);
    } catch (err) {
      console.error("Error setting active logo:", err);
    }
  };

  const handleRemoveLogo = async (logoId) => {
    const logoToRemove = allCompanyLogos.find(l => l.id === logoId);
    if (!logoToRemove) return;
    if (!window.confirm("Are you sure you want to delete this logo?")) return;
    try {
      await deleteCompanyLogo(logoId, logoToRemove.path);
    } catch (err) {
      console.error("Error removing logo:", err);
    }
  };

  const handleSavePos = async (e) => {
    e.preventDefault();
    localStorage.setItem("global_markup_rate", markupRate.toString());
    localStorage.setItem("global_po_due_date_days", dueDateDays.toString());
    localStorage.setItem("global_delayed_shipping_interval", delayedShippingInterval.toString());

    setSuccessMsg("PO settings saved successfully!");
    setTimeout(() => setSuccessMsg(""), 3000);
  };

  const handleSaveInvoicing = (e) => {
    e.preventDefault();
    try {
      localStorage.setItem("global_non_cc_discount", nonCcDiscount.toString());
      setInvoicingSuccess("Invoicing settings saved successfully!");
      setTimeout(() => setInvoicingSuccess(""), 3000);
    } catch (err) {
      console.error("Error saving invoicing settings:", err);
      setInvoicingError("Failed to save invoicing settings.");
      setTimeout(() => setInvoicingError(""), 3000);
    }
  };

  const handleSaveFacilityAddress = (e) => {
    e.preventDefault();
    try {
      const addrObj = {
        address1: facilityAddress1,
        address2: facilityAddress2,
        city: facilityCity,
        state: facilityState,
        zip: facilityZip,
        country: facilityCountry
      };
      localStorage.setItem("facility_address", JSON.stringify(addrObj));
      setAddressSuccess("Facility address saved successfully!");
      setTimeout(() => setAddressSuccess(""), 3000);
    } catch (err) {
      console.error("Error saving facility address:", err);
      setAddressError("Failed to save facility address.");
      setTimeout(() => setAddressError(""), 3000);
    }
  };

  const handleResetColumns = () => {
    if (currentUser) {
      localStorage.removeItem(`po_columns_${currentUser.uid}`);
      setSuccessMsg("PO Column order has been reset to defaults.");
      setTimeout(() => setSuccessMsg(""), 3000);
    }
  };

  
  const handleExportPNG = () => {
    const svgElement = document.querySelector('.floorplan-svg');
    if (!svgElement) return;

    const clonedSvg = svgElement.cloneNode(true);
    clonedSvg.classList.remove('drawing-mode');
    
    // Ensure styles are applied in the SVG
    const styleElement = document.createElement('style');
    styleElement.textContent = `
      :root {
        --primary: #3b82f6;
        --border-color: #475569;
        --text-primary: #ffffff;
      }
      text { font-family: sans-serif; font-weight: bold; fill: #ffffff; }
      .text-bg { fill: rgba(0,0,0,0.6); stroke: none; }
      rect[fill="url(#grid)"] { display: none; }
      svg { background-color: #111827; } /* Make sure the background isn't transparent */
    `;
    clonedSvg.insertBefore(styleElement, clonedSvg.firstChild);

    // Set a viewBox if it doesn't have one, or set a fixed width/height for the canvas
    const width = 1200;
    const height = 800;
    clonedSvg.setAttribute("width", width);
    clonedSvg.setAttribute("height", height);
    clonedSvg.setAttribute("viewBox", `0 0 ${width} ${height}`);
    clonedSvg.setAttribute("xmlns", "http://www.w3.org/2000/svg");
    
    const serializer = new XMLSerializer();
    let source = serializer.serializeToString(clonedSvg);
    source = '<?xml version="1.0" standalone="no"?>\r\n' + source;
    
    const blob = new Blob([source], { type: "image/svg+xml;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      // Draw background explicitly just in case
      ctx.fillStyle = "#111827";
      ctx.fillRect(0, 0, width, height);
      ctx.drawImage(img, 0, 0, width, height);
      
      const pngUrl = canvas.toDataURL("image/png");
      const downloadLink = document.createElement("a");
      downloadLink.href = pngUrl;
      downloadLink.download = "warehouse_floorplan.png";
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
      
      URL.revokeObjectURL(url);
    };
    img.src = url;
  };

  // Canned Messages subscription & handlers
  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, "canned_messages"), (snapshot) => {
      const list = [];
      snapshot.forEach((doc) => {
        list.push({ id: doc.id, ...doc.data() });
      });
      list.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
      setCannedMessages(list);
    });
    return () => unsubscribe();
  }, []);

  const handleSaveCannedMessage = async (e) => {
    e.preventDefault();
    if (!cannedName.trim() || !cannedContent.trim()) {
      alert("Name and Message content are required.");
      return;
    }
    try {
      if (editingCannedId) {
        const docRef = doc(db, "canned_messages", editingCannedId);
        await updateDoc(docRef, {
          name: cannedName.trim(),
          message: cannedContent.trim(),
          updatedAt: new Date().toISOString()
        });
      } else {
        await addDoc(collection(db, "canned_messages"), {
          name: cannedName.trim(),
          message: cannedContent.trim(),
          createdAt: new Date().toISOString()
        });
      }
      setCannedName("");
      setCannedContent("");
      setEditingCannedId(null);
    } catch (err) {
      console.error("Error saving canned message:", err);
      alert("Failed to save canned message.");
    }
  };

  return (
    <div className="animate-fade-in">
      <div className="module-header">
        <div>
          <h1 className="module-title">
            <SettingsIcon className="sidebar-logo-icon" style={{ marginRight: "0.5rem", verticalAlign: "middle" }} />
            System Settings
          </h1>
          <p className="module-subtitle">Configure pricing parameters, reset UI preferences, and view user details.</p>
        </div>
      </div>

      {/* Tabs navigation */}
      <div 
        className="tabs-switcher" 
        style={{ 
          display: "flex", 
          gap: "1.5rem", 
          marginBottom: "1.5rem", 
          borderBottom: "1px solid var(--border-color)",
          paddingBottom: "0.25rem"
        }}
      >
        <button
          type="button"
          onClick={() => { setActiveTab("general"); setSuccessMsg(""); }}
          style={{
            background: "none",
            border: "none",
            borderBottom: activeTab === "general" ? "2px solid var(--primary)" : "2px solid transparent",
            color: activeTab === "general" ? "var(--primary)" : "var(--text-secondary)",
            padding: "0.5rem 1rem",
            fontSize: "0.95rem",
            fontWeight: "600",
            cursor: "pointer",
            transition: "all 0.2s ease"
          }}
        >
          General
        </button>
        <button
          type="button"
          onClick={() => { setActiveTab("pos"); setSuccessMsg(""); }}
          style={{
            background: "none",
            border: "none",
            borderBottom: activeTab === "pos" ? "2px solid var(--primary)" : "2px solid transparent",
            color: activeTab === "pos" ? "var(--primary)" : "var(--text-secondary)",
            padding: "0.5rem 1rem",
            fontSize: "0.95rem",
            fontWeight: "600",
            cursor: "pointer",
            transition: "all 0.2s ease"
          }}
        >
          Procurement
        </button>
        <button
          type="button"
          onClick={() => { setActiveTab("purchase"); setSuccessMsg(""); }}
          style={{
            background: "none",
            border: "none",
            borderBottom: activeTab === "purchase" ? "2px solid var(--primary)" : "2px solid transparent",
            color: activeTab === "purchase" ? "var(--primary)" : "var(--text-secondary)",
            padding: "0.5rem 1rem",
            fontSize: "0.95rem",
            fontWeight: "600",
            cursor: "pointer",
            transition: "all 0.2s ease"
          }}
        >
          Purchase
        </button>
        <button
          type="button"
          onClick={() => { setActiveTab("logistics"); setSuccessMsg(""); }}
          style={{
            background: "none",
            border: "none",
            borderBottom: activeTab === "logistics" ? "2px solid var(--primary)" : "2px solid transparent",
            color: activeTab === "logistics" ? "var(--primary)" : "var(--text-secondary)",
            padding: "0.5rem 1rem",
            fontSize: "0.95rem",
            fontWeight: "600",
            cursor: "pointer",
            transition: "all 0.2s ease"
          }}
        >
          Logistics
        </button>
        <button
          type="button"
          onClick={() => { setActiveTab("inventory"); setSuccessMsg(""); }}
          style={{
            background: "none",
            border: "none",
            borderBottom: activeTab === "inventory" ? "2px solid var(--primary)" : "2px solid transparent",
            color: activeTab === "inventory" ? "var(--primary)" : "var(--text-secondary)",
            padding: "0.5rem 1rem",
            fontSize: "0.95rem",
            fontWeight: "600",
            cursor: "pointer",
            transition: "all 0.2s ease"
          }}
        >
          Inventory
        </button>
        <button
          type="button"
          onClick={() => { setActiveTab("invoicing"); setSuccessMsg(""); }}
          style={{
            background: "none",
            border: "none",
            borderBottom: activeTab === "invoicing" ? "2px solid var(--primary)" : "2px solid transparent",
            color: activeTab === "invoicing" ? "var(--primary)" : "var(--text-secondary)",
            padding: "0.5rem 1rem",
            fontSize: "0.95rem",
            fontWeight: "600",
            cursor: "pointer",
            transition: "all 0.2s ease"
          }}
        >
          Invoicing
        </button>
        <button
          type="button"
          onClick={() => { setActiveTab("bizdev"); setSuccessMsg(""); }}
          style={{
            background: "none",
            border: "none",
            borderBottom: activeTab === "bizdev" ? "2px solid var(--primary)" : "2px solid transparent",
            color: activeTab === "bizdev" ? "var(--primary)" : "var(--text-secondary)",
            padding: "0.5rem 1rem",
            fontSize: "0.95rem",
            fontWeight: "600",
            cursor: "pointer",
            transition: "all 0.2s ease"
          }}
        >
          Biz Dev
        </button>
        <button
          type="button"
          onClick={() => { setActiveTab("companies"); setSuccessMsg(""); }}
          style={{
            background: "none",
            border: "none",
            borderBottom: activeTab === "companies" ? "2px solid var(--primary)" : "2px solid transparent",
            color: activeTab === "companies" ? "var(--primary)" : "var(--text-secondary)",
            padding: "0.5rem 1rem",
            fontSize: "0.95rem",
            fontWeight: "600",
            cursor: "pointer",
            transition: "all 0.2s ease"
          }}
        >
          Companies
        </button>
      </div>

      <div 
        className={activeTab === "pos" ? "grid-2" : ""} 
        style={{ 
          gap: "1.5rem", 
          gridTemplateColumns: activeTab === "pos" ? "1.2fr 0.8fr" : "1fr" 
        }}
      >
        {/* Left Column Wrapper */}
        <div style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}>
          
          {activeTab === "general" ? (
            <>
              {/* Appearance preferences */}
              <div className="card glass-card">
                <div className="card-header">
                  <h3 className="card-title" style={{ display: "flex", alignItems: "center" }}>
                    <Sliders size={18} style={{ marginRight: "0.5rem", color: "var(--primary)" }} />
                    General Settings
                  </h3>
                </div>
                <form onSubmit={handleSaveGeneral} style={{ marginTop: "1rem" }}>
                  <div className="form-group">
                    <label className="form-label">Theme Accent Color</label>
                    <div style={{ display: "flex", gap: "1.5rem", marginTop: "0.5rem" }}>
                      <label style={{ display: "flex", alignItems: "center", gap: "0.5rem", cursor: "pointer" }}>
                        <input
                          type="radio"
                          name="accentColor"
                          value="default"
                          checked={accentColor === "default"}
                          onChange={(e) => setAccentColor(e.target.value)}
                        />
                        <span>Ocean Blue (Default)</span>
                      </label>
                      <label style={{ display: "flex", alignItems: "center", gap: "0.5rem", cursor: "pointer" }}>
                        <input
                          type="radio"
                          name="accentColor"
                          value="purple"
                          checked={accentColor === "purple"}
                          onChange={(e) => setAccentColor(e.target.value)}
                        />
                        <span>Royal Purple</span>
                      </label>
                      <label style={{ display: "flex", alignItems: "center", gap: "0.5rem", cursor: "pointer" }}>
                        <input
                          type="radio"
                          name="accentColor"
                          value="emerald"
                          checked={accentColor === "emerald"}
                          onChange={(e) => setAccentColor(e.target.value)}
                        />
                        <span>Emerald Green</span>
                      </label>
                    </div>
                  </div>

                  <div className="form-group" style={{ marginTop: "1.25rem", paddingTop: "1.25rem", borderTop: "1px solid var(--border-color)" }}>
                    <label className="form-label" htmlFor="defaultTaxRateInput" style={{ fontWeight: 600 }}>
                      Default Tax %
                    </label>
                    <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", maxWidth: "220px", marginTop: "0.35rem" }}>
                      <input
                        id="defaultTaxRateInput"
                        type="number"
                        step="0.01"
                        min="0"
                        max="100"
                        className="form-input"
                        value={defaultTaxRate}
                        onChange={(e) => setDefaultTaxRate(e.target.value)}
                        placeholder="0.00"
                        style={{ textAlign: "right", margin: 0 }}
                      />
                      <span style={{ color: "var(--text-secondary)", fontWeight: 600 }}>%</span>
                    </div>
                    <span style={{ fontSize: "0.8rem", color: "var(--text-muted)", marginTop: "0.4rem", display: "block" }}>
                      Default sales tax percentage used to calculate taxes on line items.
                    </span>
                  </div>

                  {successMsg && (
                    <div className="alert alert-success" style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginTop: "1rem", backgroundColor: "rgba(46, 204, 113, 0.15)", border: "1px solid #2ecc71", padding: "0.75rem", borderRadius: "4px" }}>
                      <Check size={16} className="text-success" />
                      <span>{successMsg}</span>
                    </div>
                  )}

                  <div style={{ marginTop: "1.5rem", display: "flex", gap: "1rem" }}>
                    <button type="submit" className="btn btn-primary">
                      <Save size={18} style={{ marginRight: "0.5rem" }} />
                      Save General Settings
                    </button>
                  </div>
                </form>
              </div>

              {/* Vendor Categories Management Card */}
              <div className="card glass-card">
                <div className="card-header">
                  <h3 className="card-title" style={{ display: "flex", alignItems: "center" }}>
                    <Plus size={18} style={{ marginRight: "0.5rem", color: "var(--primary)" }} />
                    Vendor Categories
                  </h3>
                </div>
                <div style={{ marginTop: "1rem" }}>
                  <p className="text-secondary" style={{ fontSize: "0.85rem", marginBottom: "1rem", lineHeight: "1.4" }}>
                    Add, edit, or remove categories. Category edits will update existing vendor records automatically.
                  </p>

                  {catError && <div className="alert alert-danger" style={{ marginBottom: "1rem" }}>{catError}</div>}
                  {catSuccess && <div className="alert alert-success" style={{ marginBottom: "1rem" }}>{catSuccess}</div>}

                  <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem", maxHeight: "300px", overflowY: "auto", paddingRight: "0.5rem", marginBottom: "1.5rem" }}>
                    {categories.map((cat) => (
                      <div 
                        key={cat.id} 
                        style={{ 
                          display: "flex", 
                          alignItems: "center", 
                          justifyContent: "space-between", 
                          padding: "0.5rem 0.75rem", 
                          backgroundColor: "rgba(255, 255, 255, 0.02)", 
                          borderRadius: "4px", 
                          border: "1px solid var(--border-color)" 
                        }}
                      >
                        {editingCatId === cat.id ? (
                          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", width: "100%" }}>
                            <input
                              type="text"
                              className="form-input"
                              value={editingCatName}
                              onChange={(e) => setEditingCatName(e.target.value)}
                              style={{ margin: 0, padding: "0.25rem 0.5rem", color: "var(--text-primary)", backgroundColor: "var(--bg-input)" }}
                              autoFocus
                            />
                            <button 
                              type="button" 
                              className="btn btn-secondary btn-icon" 
                              onClick={() => handleEditCategory(cat.id, cat.name)}
                              style={{ padding: "0.25rem", minHeight: "auto", border: "1px solid var(--primary)", color: "var(--primary)" }}
                              title="Save Name"
                            >
                              <Check size={16} />
                            </button>
                            <button 
                              type="button" 
                              className="btn btn-secondary btn-icon" 
                              onClick={() => setEditingCatId(null)}
                              style={{ padding: "0.25rem", minHeight: "auto" }}
                              title="Cancel"
                            >
                              <X size={16} />
                            </button>
                          </div>
                        ) : (
                          <>
                            <span style={{ fontWeight: "500", color: "var(--text-primary)" }}>{cat.name}</span>
                            <div style={{ display: "flex", gap: "0.4rem" }}>
                              <button
                                type="button"
                                className="btn btn-secondary btn-icon status-action-btn"
                                onClick={() => {
                                  setEditingCatId(cat.id);
                                  setEditingCatName(cat.name);
                                }}
                                style={{ margin: 0, padding: "0.25rem" }}
                                title="Edit Category"
                              >
                                <Edit size={14} />
                              </button>
                              <button
                                type="button"
                                className="btn btn-secondary btn-icon status-action-btn"
                                style={{ margin: 0, padding: "0.25rem", borderColor: "var(--danger)", color: "var(--danger)" }}
                                onClick={() => handleDeleteCategory(cat.id, cat.name)}
                                title="Delete Category"
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Add Category Form */}
                  <form onSubmit={handleAddCategory} style={{ display: "flex", gap: "0.5rem" }}>
                    <input
                      type="text"
                      placeholder="e.g. Courier Logistics"
                      className="form-input"
                      value={newCatName}
                      onChange={(e) => setNewCatName(e.target.value)}
                      style={{ margin: 0, color: "var(--text-primary)", backgroundColor: "var(--bg-input)" }}
                      required
                    />
                    <button type="submit" className="btn btn-primary" style={{ whiteSpace: "nowrap" }}>
                      <Plus size={18} />
                      Add Category
                    </button>
                  </form>
                </div>
              </div>

              {/* Product Tags Management Card */}
              <div className="card glass-card" style={{ overflow: "visible" }}>
                <div className="card-header">
                  <h3 className="card-title" style={{ display: "flex", alignItems: "center" }}>
                    <Tag size={18} style={{ marginRight: "0.5rem", color: "var(--primary)" }} />
                    Product Tags
                  </h3>
                </div>
                <div style={{ marginTop: "1rem" }}>
                  <p className="text-secondary" style={{ fontSize: "0.85rem", marginBottom: "1rem", lineHeight: "1.4" }}>
                    Add, edit, or remove product tags. Tag edits/deletions will update existing vendor records automatically.
                  </p>

                  {tagError && <div className="alert alert-danger" style={{ marginBottom: "1rem" }}>{tagError}</div>}
                  {tagSuccess && <div className="alert alert-success" style={{ marginBottom: "1rem" }}>{tagSuccess}</div>}

                  <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem", maxHeight: "300px", overflowY: "auto", paddingRight: "0.5rem", marginBottom: "1.5rem" }}>
                    {tags.map((tag) => (
                      <div 
                        key={tag.id} 
                        style={{ 
                          display: "flex", 
                          alignItems: "center", 
                          justifyContent: "space-between", 
                          padding: "0.5rem 0.75rem", 
                          backgroundColor: "rgba(255, 255, 255, 0.02)", 
                          borderRadius: "4px", 
                          border: "1px solid var(--border-color)" 
                        }}
                      >
                        {editingTagId === tag.id ? (
                          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", width: "100%" }}>
                            <input
                              type="text"
                              className="form-input"
                              value={editingTagName}
                              onChange={(e) => setEditingTagName(e.target.value)}
                              style={{ margin: 0, padding: "0.25rem 0.5rem", color: "var(--text-primary)", backgroundColor: "var(--bg-input)" }}
                              autoFocus
                            />
                            <button 
                              type="button" 
                              className="btn btn-secondary btn-icon" 
                              onClick={() => handleEditTag(tag.id, tag.name)}
                              style={{ padding: "0.25rem", minHeight: "auto", border: "1px solid var(--primary)", color: "var(--primary)" }}
                              title="Save Name"
                            >
                              <Check size={16} />
                            </button>
                            <button 
                              type="button" 
                              className="btn btn-secondary btn-icon" 
                              onClick={() => setEditingTagId(null)}
                              style={{ padding: "0.25rem", minHeight: "auto" }}
                              title="Cancel"
                            >
                              <X size={16} />
                            </button>
                          </div>
                        ) : (
                          <>
                            <span style={{ fontWeight: "500", color: "var(--text-primary)" }}>{tag.name}</span>
                            <div style={{ display: "flex", gap: "0.4rem" }}>
                              <button
                                type="button"
                                className="btn btn-secondary btn-icon status-action-btn"
                                onClick={() => {
                                  setEditingTagId(tag.id);
                                  setEditingTagName(tag.name);
                                }}
                                style={{ margin: 0, padding: "0.25rem" }}
                                title="Edit Tag"
                              >
                                <Edit size={14} />
                              </button>
                              <button
                                type="button"
                                className="btn btn-secondary btn-icon status-action-btn"
                                style={{ margin: 0, padding: "0.25rem", borderColor: "var(--danger)", color: "var(--danger)" }}
                                onClick={() => handleDeleteTag(tag.id, tag.name)}
                                title="Delete Tag"
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Add Tag Form */}
                  <div style={{ position: "relative" }}>
                    <form onSubmit={handleAddTag} style={{ display: "flex", gap: "0.5rem" }}>
                      <input
                        type="text"
                        placeholder="e.g. Specialized Valves"
                        className="form-input"
                        value={newTagName}
                        onChange={(e) => {
                          setNewTagName(e.target.value);
                          setShowTagSuggestions(true);
                        }}
                        onFocus={() => setShowTagSuggestions(true)}
                        onBlur={() => setTimeout(() => setShowTagSuggestions(false), 200)}
                        style={{ margin: 0, color: "var(--text-primary)", backgroundColor: "var(--bg-input)" }}
                        required
                      />
                      <button type="submit" className="btn btn-primary" style={{ whiteSpace: "nowrap" }}>
                        <Plus size={18} />
                        Add Tag
                      </button>
                    </form>

                    {/* Suggestions Overlay Dropdown */}
                    {showTagSuggestions && newTagName.trim() && (
                      <div className="tag-suggestions-dropdown" style={{ 
                        position: "absolute", 
                        bottom: "100%", 
                        left: 0, 
                        right: 0, 
                        backgroundColor: "var(--bg-card)", 
                        border: "1px solid var(--border-color)", 
                        borderRadius: "4px", 
                        zIndex: 1000, 
                        maxHeight: "150px", 
                        overflowY: "auto", 
                        marginBottom: "0.25rem",
                        boxShadow: "0 -4px 12px rgba(0,0,0,0.5)"
                      }}>
                        {EXCEL_PRODUCT_TAGS
                          .filter(t => 
                            t.toLowerCase().includes(newTagName.toLowerCase())
                          )
                          .sort((a, b) => a.localeCompare(b))
                          .slice(0, 50)
                          .map(tag => {
                            const exists = tags.some(existing => existing.name.toLowerCase() === tag.toLowerCase());
                            return (
                              <div
                                key={tag}
                                onMouseDown={() => {
                                  if (!exists) {
                                    setNewTagName(tag);
                                    setShowTagSuggestions(false);
                                  }
                                }}
                                className={exists ? "suggestion-item-disabled" : "suggestion-item"}
                                style={{ 
                                  padding: "0.5rem 0.75rem", 
                                  cursor: exists ? "not-allowed" : "pointer", 
                                  color: exists ? "var(--text-muted)" : "var(--text-primary)",
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  opacity: exists ? 0.6 : 1
                                }}
                              >
                                <span>{tag}</span>
                                {exists && <span style={{ fontSize: "0.75rem", color: "var(--text-muted)", fontStyle: "italic" }}>Already added</span>}
                              </div>
                            );
                          })
                        }
                        {EXCEL_PRODUCT_TAGS.filter(t => 
                          t.toLowerCase().includes(newTagName.toLowerCase())
                        ).length === 0 && (
                          <div style={{ padding: "0.5rem 0.75rem", color: "var(--text-muted)", fontSize: "0.85rem" }}>
                            Press Enter to add new custom tag
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Brand Tags Management Card */}
              <div className="card glass-card" style={{ overflow: "visible" }}>
                <div className="card-header">
                  <h3 className="card-title" style={{ display: "flex", alignItems: "center" }}>
                    <Tag size={18} style={{ marginRight: "0.5rem", color: "var(--primary)" }} />
                    Brand Tags
                  </h3>
                </div>
                <div style={{ marginTop: "1rem" }}>
                  <p className="text-secondary" style={{ fontSize: "0.85rem", marginBottom: "1rem", lineHeight: "1.4" }}>
                    Add, edit, or remove brand tags. Brand tag edits/deletions will update existing vendor records automatically.
                  </p>

                  {brandTagError && <div className="alert alert-danger" style={{ marginBottom: "1rem" }}>{brandTagError}</div>}
                  {brandTagSuccess && <div className="alert alert-success" style={{ marginBottom: "1rem" }}>{brandTagSuccess}</div>}

                  <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem", maxHeight: "300px", overflowY: "auto", paddingRight: "0.5rem", marginBottom: "1.5rem" }}>
                    {brandTags.map((tag) => (
                      <div 
                        key={tag.id} 
                        style={{ 
                          display: "flex", 
                          alignItems: "center", 
                          justifyContent: "space-between", 
                          padding: "0.5rem 0.75rem", 
                          backgroundColor: "rgba(255, 255, 255, 0.02)", 
                          borderRadius: "4px", 
                          border: "1px solid var(--border-color)" 
                        }}
                      >
                        {editingBrandTagId === tag.id ? (
                          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", width: "100%" }}>
                            <input
                              type="text"
                              className="form-input"
                              value={editingBrandTagName}
                              onChange={(e) => setEditingBrandTagName(e.target.value)}
                              style={{ margin: 0, padding: "0.25rem 0.5rem", color: "var(--text-primary)", backgroundColor: "var(--bg-input)" }}
                              autoFocus
                            />
                            <button 
                              type="button" 
                              className="btn btn-secondary btn-icon" 
                              onClick={() => handleEditBrandTag(tag.id, tag.name)}
                              style={{ padding: "0.25rem", minHeight: "auto", border: "1px solid var(--primary)", color: "var(--primary)" }}
                              title="Save Name"
                            >
                              <Check size={16} />
                            </button>
                            <button 
                              type="button" 
                              className="btn btn-secondary btn-icon" 
                              onClick={() => setEditingBrandTagId(null)}
                              style={{ padding: "0.25rem", minHeight: "auto" }}
                              title="Cancel"
                            >
                              <X size={16} />
                            </button>
                          </div>
                        ) : (
                          <>
                            <span style={{ fontWeight: "500", color: "var(--text-primary)" }}>{tag.name}</span>
                            <div style={{ display: "flex", gap: "0.4rem" }}>
                              <button
                                type="button"
                                className="btn btn-secondary btn-icon status-action-btn"
                                onClick={() => {
                                  setEditingBrandTagId(tag.id);
                                  setEditingBrandTagName(tag.name);
                                }}
                                style={{ margin: 0, padding: "0.25rem" }}
                                title="Edit Brand Tag"
                              >
                                <Edit size={14} />
                              </button>
                              <button
                                type="button"
                                className="btn btn-secondary btn-icon status-action-btn"
                                style={{ margin: 0, padding: "0.25rem", borderColor: "var(--danger)", color: "var(--danger)" }}
                                onClick={() => handleDeleteBrandTag(tag.id, tag.name)}
                                title="Delete Brand Tag"
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Add Brand Tag Form */}
                  <form onSubmit={handleAddBrandTag} style={{ display: "flex", gap: "0.5rem" }}>
                    <input
                      type="text"
                      placeholder="e.g. Rockwell Automation"
                      className="form-input"
                      value={newBrandTagName}
                      onChange={(e) => setNewBrandTagName(e.target.value)}
                      style={{ margin: 0, color: "var(--text-primary)", backgroundColor: "var(--bg-input)" }}
                      required
                    />
                    <button type="submit" className="btn btn-primary" style={{ whiteSpace: "nowrap" }}>
                      <Plus size={18} />
                      Add Brand Tag
                    </button>
                  </form>
                </div>
              </div>
          </>
          ) : activeTab === "pos" ? (
            <>
              {/* Estimates Configuration Settings */}
              <div className="card glass-card">
                <div className="card-header">
                  <h3 className="card-title" style={{ display: "flex", alignItems: "center" }}>
                    <Sliders size={18} style={{ marginRight: "0.5rem", color: "var(--primary)" }} />
                    Estimates System Settings
                  </h3>
                </div>
                <form onSubmit={handleSavePos} style={{ marginTop: "1rem" }}>
                  <div className="form-group">
                    <label className="form-label" htmlFor="markupRate">Default Bid Markup Percentage (%)</label>
                    <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
                      <input
                        id="markupRate"
                        type="number"
                        min="0"
                        max="100"
                        className="form-input"
                        value={markupRate}
                        onChange={(e) => setMarkupRate(parseFloat(e.target.value) || 0)}
                        onWheel={(e) => e.target.blur()}
                        style={{ width: "120px" }}
                      />
                      <span className="text-secondary" style={{ fontSize: "0.9rem" }}>
                        Proposed Client Charge = Total Cost * {(1 + markupRate/100).toFixed(2)}
                      </span>
                    </div>
                  </div>

                  <div className="form-group" style={{ marginTop: "1.5rem" }}>
                    <label className="form-label" htmlFor="dueDateDays">Due Date Offset (Days)</label>
                    <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
                      <input
                        id="dueDateDays"
                        type="number"
                        min="0"
                        className="form-input"
                        value={dueDateDays}
                        onChange={(e) => setDueDateDays(parseInt(e.target.value, 10) || 0)}
                        onWheel={(e) => e.target.blur()}
                        style={{ width: "120px" }}
                      />
                      <span className="text-secondary" style={{ fontSize: "0.9rem" }}>
                        PO Due Date defaults to Request Date + {dueDateDays} days.
                      </span>
                    </div>
                  </div>

                  <div className="form-group" style={{ marginTop: "1.5rem" }}>
                    <label className="form-label" htmlFor="delayedShippingInterval">Delayed Shipping Interval (Days)</label>
                    <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
                      <input
                        id="delayedShippingInterval"
                        type="number"
                        min="0"
                        className="form-input"
                        value={delayedShippingInterval}
                        onChange={(e) => setDelayedShippingInterval(parseInt(e.target.value, 10) || 0)}
                        onWheel={(e) => e.target.blur()}
                        style={{ width: "120px" }}
                      />
                      <span className="text-secondary" style={{ fontSize: "0.9rem" }}>
                        Flag shipments delayed beyond {delayedShippingInterval} days past ETA.
                      </span>
                    </div>
                  </div>

                  {successMsg && (
                    <div className="alert alert-success" style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginTop: "1rem", backgroundColor: "rgba(46, 204, 113, 0.15)", border: "1px solid #2ecc71", padding: "0.75rem", borderRadius: "4px" }}>
                      <Check size={16} className="text-success" />
                      <span>{successMsg}</span>
                    </div>
                  )}

                  <div style={{ marginTop: "2rem", display: "flex", gap: "1rem" }}>
                    <button type="submit" className="btn btn-primary">
                      <Save size={18} style={{ marginRight: "0.5rem" }} />
                      Save Estimates Settings
                    </button>
                  </div>
                </form>
              </div>

              {/* Canned Messages Section */}
              <div className="card glass-card" style={{ marginTop: "1.5rem" }}>
                <div className="card-header">
                  <h3 className="card-title" style={{ display: "flex", alignItems: "center" }}>
                    <Sliders size={18} style={{ marginRight: "0.5rem", color: "var(--primary)" }} />
                    Canned Messages
                  </h3>
                </div>
                
                <div style={{ padding: "1.5rem" }}>
                  {/* Form to Add/Edit Canned Message */}
                  <form onSubmit={handleSaveCannedMessage} style={{ marginBottom: "2rem", borderBottom: "1px solid var(--border-color)", paddingBottom: "1.5rem" }}>
                    <h4 style={{ color: "var(--text-primary)", marginBottom: "1rem" }}>
                      {editingCannedId ? "Edit Canned Message" : "Add Canned Message"}
                    </h4>
                    <div className="form-group">
                      <label className="form-label" htmlFor="cannedName">Name</label>
                      <input
                        id="cannedName"
                        type="text"
                        className="form-input"
                        placeholder="e.g. Standard Terms"
                        value={cannedName}
                        onChange={(e) => setCannedName(e.target.value)}
                        required
                        style={{ maxWidth: "400px" }}
                      />
                    </div>
                    <div className="form-group" style={{ marginTop: "1rem" }}>
                      <label className="form-label" htmlFor="cannedContent">Message</label>
                      <textarea
                        id="cannedContent"
                        className="form-input"
                        placeholder="Type canned message here..."
                        value={cannedContent}
                        onChange={(e) => setCannedContent(e.target.value)}
                        required
                        rows="4"
                        style={{ width: "100%", fontFamily: "inherit" }}
                      />
                    </div>
                    <div style={{ marginTop: "1.25rem", display: "flex", gap: "0.75rem" }}>
                      <button type="submit" className="btn btn-primary" style={{ padding: "0.4rem 1rem", fontSize: "0.85rem" }}>
                        {editingCannedId ? "Update Message" : "Add Message"}
                      </button>
                      {editingCannedId && (
                        <button
                          type="button"
                          className="btn btn-secondary"
                          style={{ padding: "0.4rem 1rem", fontSize: "0.85rem" }}
                          onClick={() => {
                            setEditingCannedId(null);
                            setCannedName("");
                            setCannedContent("");
                          }}
                        >
                          Cancel
                        </button>
                      )}
                    </div>
                  </form>

                  {/* List of Canned Messages */}
                  <h4 style={{ color: "var(--text-primary)", marginBottom: "1rem" }}>Configured Messages</h4>
                  {cannedMessages.length === 0 ? (
                    <p style={{ color: "var(--text-secondary)", fontSize: "0.9rem", fontStyle: "italic" }}>No canned messages configured yet.</p>
                  ) : (
                    <div className="table-responsive">
                      <table className="reports-table" style={{ width: "100%", borderCollapse: "collapse" }}>
                        <thead>
                          <tr>
                            <th>Name</th>
                            <th>Message Preview</th>
                            <th style={{ width: "120px", textAlign: "right" }}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {cannedMessages.map(msg => (
                            <tr key={msg.id} style={{ borderBottom: "1px solid var(--border-color)" }}>
                              <td style={{ fontWeight: "600", color: "var(--text-primary)" }}>{msg.name}</td>
                              <td style={{ maxWidth: "400px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: "var(--text-secondary)" }}>
                                {msg.message}
                              </td>
                              <td style={{ textAlign: "right" }}>
                                <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem" }}>
                                  <button
                                    type="button"
                                    className="btn btn-secondary"
                                    style={{ padding: "0.3rem", display: "flex", alignItems: "center", justifyContent: "center" }}
                                    onClick={() => {
                                      setEditingCannedId(msg.id);
                                      setCannedName(msg.name);
                                      setCannedContent(msg.message);
                                    }}
                                    title="Edit"
                                  >
                                    <Edit size={14} />
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-secondary"
                                    style={{ padding: "0.3rem", color: "var(--danger)", borderColor: "transparent", display: "flex", alignItems: "center", justifyContent: "center" }}
                                    onClick={async () => {
                                      if (window.confirm(`Are you sure you want to delete "${msg.name}"?`)) {
                                        try {
                                          await deleteDoc(doc(db, "canned_messages", msg.id));
                                        } catch (err) {
                                          console.error("Error deleting canned message:", err);
                                        }
                                      }
                                    }}
                                    title="Delete"
                                  >
                                    <Trash2 size={14} />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>

              {/* Customer Payment Methods Management Card */}
              <div className="card glass-card" style={{ marginTop: "1.5rem" }}>
                <div className="card-header">
                  <h3 className="card-title" style={{ display: "flex", alignItems: "center" }}>
                    <CreditCard size={18} style={{ marginRight: "0.5rem", color: "var(--primary)" }} />
                    Payment Methods
                  </h3>
                </div>
                <div style={{ marginTop: "1rem" }}>
                  <p className="text-secondary" style={{ fontSize: "0.85rem", marginBottom: "1rem", lineHeight: "1.4" }}>
                    Add, edit, or remove customer payment methods. These are used when creating Estimates and managing Customer profiles in CRM. Edits will sync with customer profiles automatically.
                  </p>

                  {payMethodError && <div className="alert alert-danger" style={{ marginBottom: "1rem" }}>{payMethodError}</div>}
                  {payMethodSuccess && <div className="alert alert-success" style={{ marginBottom: "1rem" }}>{payMethodSuccess}</div>}

                  <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem", maxHeight: "350px", overflowY: "auto", paddingRight: "0.5rem", marginBottom: "1.5rem" }}>
                    {paymentMethods.map((pm) => (
                      <div 
                        key={pm.id} 
                        style={{ 
                          display: "flex", 
                          alignItems: "center", 
                          justifyContent: "space-between", 
                          padding: "0.5rem 0.75rem", 
                          backgroundColor: "rgba(255, 255, 255, 0.02)", 
                          borderRadius: "4px", 
                          border: "1px solid var(--border-color)" 
                        }}
                      >
                        {editingPayMethodId === pm.id ? (
                          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", width: "100%" }}>
                            <input
                              type="text"
                              className="form-input"
                              value={editingPayMethodName}
                              onChange={(e) => setEditingPayMethodName(e.target.value)}
                              style={{ margin: 0, padding: "0.25rem 0.5rem", color: "var(--text-primary)", backgroundColor: "var(--bg-input)" }}
                              autoFocus
                            />
                            <button 
                              type="button" 
                              className="btn btn-secondary btn-icon" 
                              onClick={() => handleEditPaymentMethod(pm.id, pm.name)}
                              style={{ padding: "0.25rem", minHeight: "auto", border: "1px solid var(--primary)", color: "var(--primary)" }}
                              title="Save Name"
                            >
                              <Check size={16} />
                            </button>
                            <button 
                              type="button" 
                              className="btn btn-secondary btn-icon" 
                              onClick={() => setEditingPayMethodId(null)}
                              style={{ padding: "0.25rem", minHeight: "auto" }}
                              title="Cancel"
                            >
                              <X size={16} />
                            </button>
                          </div>
                        ) : (
                          <>
                            <span style={{ fontWeight: "500", color: "var(--text-primary)" }}>{pm.name}</span>
                            <div style={{ display: "flex", gap: "0.4rem" }}>
                              <button
                                type="button"
                                className="btn btn-secondary btn-icon status-action-btn"
                                onClick={() => {
                                  setEditingPayMethodId(pm.id);
                                  setEditingPayMethodName(pm.name);
                                }}
                                style={{ margin: 0, padding: "0.25rem" }}
                                title="Edit Payment Method"
                              >
                                <Edit size={14} />
                              </button>
                              <button
                                type="button"
                                className="btn btn-secondary btn-icon status-action-btn"
                                style={{ margin: 0, padding: "0.25rem", borderColor: "var(--danger)", color: "var(--danger)" }}
                                onClick={() => handleDeletePaymentMethod(pm.id, pm.name)}
                                title="Delete Payment Method"
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Add Payment Method Form */}
                  <form onSubmit={handleAddPaymentMethod} style={{ display: "flex", gap: "0.5rem" }}>
                    <input
                      type="text"
                      placeholder="e.g. Net 30, Wire Transfer, Credit Card"
                      className="form-input"
                      value={newPayMethodName}
                      onChange={(e) => setNewPayMethodName(e.target.value)}
                      style={{ margin: 0, color: "var(--text-primary)", backgroundColor: "var(--bg-input)" }}
                      required
                    />
                    <button type="submit" className="btn btn-primary" style={{ whiteSpace: "nowrap" }}>
                      <Plus size={18} />
                      Add Method
                    </button>
                  </form>
                </div>
              </div>
            </>
          ) : activeTab === "purchase" ? (
            <>
              {/* Internal Payment Methods Management Card */}
              <div className="card glass-card">
                <div className="card-header">
                  <h3 className="card-title" style={{ display: "flex", alignItems: "center" }}>
                    <CreditCard size={18} style={{ marginRight: "0.5rem", color: "var(--primary)" }} />
                    Internal Payment Method
                  </h3>
                </div>
                <div style={{ marginTop: "1rem" }}>
                  <p className="text-secondary" style={{ fontSize: "0.85rem", marginBottom: "1rem", lineHeight: "1.4" }}>
                    Add, edit, or remove internal payment methods used when purchasing from vendors and suppliers.
                  </p>

                  {internalPayMethodError && <div className="alert alert-danger" style={{ marginBottom: "1rem" }}>{internalPayMethodError}</div>}
                  {internalPayMethodSuccess && <div className="alert alert-success" style={{ marginBottom: "1rem" }}>{internalPayMethodSuccess}</div>}

                  <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem", maxHeight: "350px", overflowY: "auto", paddingRight: "0.5rem", marginBottom: "1.5rem" }}>
                    {internalPaymentMethods.map((pm) => (
                      <div 
                        key={pm.id} 
                        style={{ 
                          display: "flex", 
                          alignItems: "center", 
                          justifyContent: "space-between", 
                          padding: "0.5rem 0.75rem", 
                          backgroundColor: "rgba(255, 255, 255, 0.02)", 
                          borderRadius: "4px", 
                          border: "1px solid var(--border-color)" 
                        }}
                      >
                        {editingInternalPayMethodId === pm.id ? (
                          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", width: "100%" }}>
                            <input
                              type="text"
                              className="form-input"
                              value={editingInternalPayMethodName}
                              onChange={(e) => setEditingInternalPayMethodName(e.target.value)}
                              style={{ margin: 0, padding: "0.25rem 0.5rem", color: "var(--text-primary)", backgroundColor: "var(--bg-input)" }}
                              autoFocus
                            />
                            <button 
                              type="button" 
                              className="btn btn-secondary btn-icon" 
                              onClick={() => handleEditInternalPaymentMethod(pm.id, pm.name)}
                              style={{ padding: "0.25rem", minHeight: "auto", border: "1px solid var(--primary)", color: "var(--primary)" }}
                              title="Save Name"
                            >
                              <Check size={16} />
                            </button>
                            <button 
                              type="button" 
                              className="btn btn-secondary btn-icon" 
                              onClick={() => setEditingInternalPayMethodId(null)}
                              style={{ padding: "0.25rem", minHeight: "auto" }}
                              title="Cancel"
                            >
                              <X size={16} />
                            </button>
                          </div>
                        ) : (
                          <>
                            <span style={{ fontWeight: "500", color: "var(--text-primary)" }}>{pm.name}</span>
                            <div style={{ display: "flex", gap: "0.4rem" }}>
                              <button
                                type="button"
                                className="btn btn-secondary btn-icon status-action-btn"
                                onClick={() => {
                                  setEditingInternalPayMethodId(pm.id);
                                  setEditingInternalPayMethodName(pm.name);
                                }}
                                style={{ margin: 0, padding: "0.25rem" }}
                                title="Edit Internal Payment Method"
                              >
                                <Edit size={14} />
                              </button>
                              <button
                                type="button"
                                className="btn btn-secondary btn-icon status-action-btn"
                                style={{ margin: 0, padding: "0.25rem", borderColor: "var(--danger)", color: "var(--danger)" }}
                                onClick={() => handleDeleteInternalPaymentMethod(pm.id, pm.name)}
                                title="Delete Internal Payment Method"
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Add Internal Payment Method Form */}
                  <form onSubmit={handleAddInternalPaymentMethod} style={{ display: "flex", gap: "0.5rem" }}>
                    <input
                      type="text"
                      placeholder="e.g. Corporate Amex, Bank Wire, ACH"
                      className="form-input"
                      value={newInternalPayMethodName}
                      onChange={(e) => setNewInternalPayMethodName(e.target.value)}
                      style={{ margin: 0, color: "var(--text-primary)", backgroundColor: "var(--bg-input)" }}
                      required
                    />
                    <button type="submit" className="btn btn-primary" style={{ whiteSpace: "nowrap" }}>
                      <Plus size={18} />
                      Add Method
                    </button>
                  </form>
                </div>
              </div>
            </>
          ) : activeTab === "logistics" ? (
            <>
              {/* Logistics Carriers Card */}
              <div className="card glass-card">
                <div className="card-header">
                  <h3 className="card-title" style={{ display: "flex", alignItems: "center" }}>
                    <Truck size={18} style={{ marginRight: "0.5rem", color: "var(--primary)" }} />
                    Carriers Management
                  </h3>
                </div>
                <div style={{ marginTop: "1rem" }}>
                  <p className="text-secondary" style={{ fontSize: "0.85rem", marginBottom: "1rem", lineHeight: "1.4" }}>
                    Add, edit, or remove shipping carriers. These carriers will be selectable when dispatching cargo shipments.
                  </p>

                  {carrierError && <div className="alert alert-danger" style={{ marginBottom: "1rem" }}>{carrierError}</div>}
                  {carrierSuccess && <div className="alert alert-success" style={{ marginBottom: "1rem" }}>{carrierSuccess}</div>}

                  <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem", maxHeight: "350px", overflowY: "auto", paddingRight: "0.5rem", marginBottom: "1.5rem" }}>
                    {carriersList.map((carrier) => (
                      <div 
                        key={carrier.id} 
                        style={{ 
                          display: "flex", 
                          alignItems: "center", 
                          justifyContent: "space-between", 
                          padding: "0.5rem 0.75rem", 
                          backgroundColor: "rgba(255, 255, 255, 0.02)", 
                          borderRadius: "4px", 
                          border: "1px solid var(--border-color)" 
                        }}
                      >
                        {editingCarrierId === carrier.id ? (
                          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", width: "100%" }}>
                            <input
                              type="text"
                              className="form-input"
                              value={editingCarrierName}
                              onChange={(e) => setEditingCarrierName(e.target.value)}
                              style={{ margin: 0, padding: "0.25rem 0.5rem", color: "var(--text-primary)", backgroundColor: "var(--bg-input)" }}
                              autoFocus
                            />
                            <button 
                              type="button" 
                              className="btn btn-secondary btn-icon" 
                              onClick={() => handleEditCarrier(carrier.id, carrier.name)}
                              style={{ padding: "0.25rem", minHeight: "auto", border: "1px solid var(--primary)", color: "var(--primary)" }}
                              title="Save Carrier"
                            >
                              <Check size={16} />
                            </button>
                            <button 
                              type="button" 
                              className="btn btn-secondary btn-icon" 
                              onClick={() => setEditingCarrierId(null)}
                              style={{ padding: "0.25rem", minHeight: "auto" }}
                              title="Cancel"
                            >
                              <X size={16} />
                            </button>
                          </div>
                        ) : (
                          <>
                            <span style={{ fontWeight: "500", color: "var(--text-primary)" }}>{carrier.name}</span>
                            <div style={{ display: "flex", gap: "0.4rem" }}>
                              <button
                                type="button"
                                className="btn btn-secondary btn-icon status-action-btn"
                                onClick={() => {
                                  setEditingCarrierId(carrier.id);
                                  setEditingCarrierName(carrier.name);
                                }}
                                style={{ margin: 0, padding: "0.25rem" }}
                                title="Edit Carrier"
                              >
                                <Edit size={14} />
                              </button>
                              <button
                                type="button"
                                className="btn btn-secondary btn-icon status-action-btn"
                                style={{ margin: 0, padding: "0.25rem", borderColor: "var(--danger)", color: "var(--danger)" }}
                                onClick={() => handleDeleteCarrier(carrier.id, carrier.name)}
                                title="Delete Carrier"
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Add Carrier Form */}
                  <form onSubmit={handleAddCarrier} style={{ display: "flex", gap: "0.5rem" }}>
                    <input
                      type="text"
                      placeholder="e.g. UPS Logistics"
                      className="form-input"
                      value={newCarrierName}
                      onChange={(e) => setNewCarrierName(e.target.value)}
                      style={{ margin: 0, color: "var(--text-primary)", backgroundColor: "var(--bg-input)" }}
                      required
                    />
                    <button type="submit" className="btn btn-primary" style={{ whiteSpace: "nowrap" }}>
                      <Plus size={18} />
                      Add Carrier
                    </button>
                  </form>
                </div>
              </div>

              {/* Facility Address Card */}
              <div className="card glass-card" style={{ marginTop: "1.5rem" }}>
                <div className="card-header">
                  <h3 className="card-title" style={{ display: "flex", alignItems: "center" }}>
                    <MapPin size={18} style={{ marginRight: "0.5rem", color: "var(--primary)" }} />
                    Facility Address Settings
                  </h3>
                </div>
                <form onSubmit={handleSaveFacilityAddress} style={{ marginTop: "1rem" }}>
                  <p className="text-secondary" style={{ fontSize: "0.85rem", marginBottom: "1rem", lineHeight: "1.4" }}>
                    Configure the default facility location address.
                  </p>

                  {addressError && <div className="alert alert-danger" style={{ marginBottom: "1rem" }}>{addressError}</div>}
                  {addressSuccess && <div className="alert alert-success" style={{ marginBottom: "1rem" }}>{addressSuccess}</div>}

                  <div className="form-group" style={{ marginBottom: "1rem" }}>
                    <label className="form-label" htmlFor="facilityAddress1">Address 1</label>
                    <input
                      id="facilityAddress1"
                      type="text"
                      placeholder="123 Main St"
                      className="form-input"
                      value={facilityAddress1}
                      onChange={(e) => setFacilityAddress1(e.target.value)}
                      style={{ color: "var(--text-primary)", backgroundColor: "var(--bg-input)" }}
                      required
                    />
                  </div>

                  <div className="form-group" style={{ marginBottom: "1rem" }}>
                    <label className="form-label" htmlFor="facilityAddress2">Address 2 (Optional)</label>
                    <input
                      id="facilityAddress2"
                      type="text"
                      placeholder="Suite 100"
                      className="form-input"
                      value={facilityAddress2}
                      onChange={(e) => setFacilityAddress2(e.target.value)}
                      style={{ color: "var(--text-primary)", backgroundColor: "var(--bg-input)" }}
                    />
                  </div>

                  <div className="grid-2" style={{ gap: "1rem", gridTemplateColumns: "1fr 1fr", marginBottom: "1rem" }}>
                    <div className="form-group">
                      <label className="form-label" htmlFor="facilityCity">City</label>
                      <input
                        id="facilityCity"
                        type="text"
                        placeholder="Chicago"
                        className="form-input"
                        value={facilityCity}
                        onChange={(e) => setFacilityCity(e.target.value)}
                        style={{ color: "var(--text-primary)", backgroundColor: "var(--bg-input)" }}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label" htmlFor="facilityState">State / Province</label>
                      <input
                        id="facilityState"
                        type="text"
                        placeholder="IL"
                        className="form-input"
                        value={facilityState}
                        onChange={(e) => setFacilityState(e.target.value)}
                        style={{ color: "var(--text-primary)", backgroundColor: "var(--bg-input)" }}
                        required
                      />
                    </div>
                  </div>

                  <div className="grid-2" style={{ gap: "1rem", gridTemplateColumns: "1fr 1fr", marginBottom: "1.5rem" }}>
                    <div className="form-group">
                      <label className="form-label" htmlFor="facilityZip">ZIP / Postal Code</label>
                      <input
                        id="facilityZip"
                        type="text"
                        placeholder="60601"
                        className="form-input"
                        value={facilityZip}
                        onChange={(e) => setFacilityZip(e.target.value)}
                        style={{ color: "var(--text-primary)", backgroundColor: "var(--bg-input)" }}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label" htmlFor="facilityCountry">Country</label>
                      <input
                        id="facilityCountry"
                        type="text"
                        placeholder="United States"
                        className="form-input"
                        value={facilityCountry}
                        onChange={(e) => setFacilityCountry(e.target.value)}
                        style={{ color: "var(--text-primary)", backgroundColor: "var(--bg-input)" }}
                        required
                      />
                    </div>
                  </div>

                  <div style={{ display: "flex", gap: "1rem" }}>
                    <button type="submit" className="btn btn-primary">
                      <Save size={18} style={{ marginRight: "0.5rem" }} />
                      Save Address
                    </button>
                  </div>
                </form>
              </div>
            </>
          ) : activeTab === "inventory" ? (
            <div className="settings-panel animate-fade-in" style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <h2 style={{ fontSize: "1.1rem", fontWeight: "600", color: "var(--text-primary)", display: "flex", alignItems: "center", gap: "0.5rem" }}>
                  <MapPin size={18} className="text-primary" />
                  Warehouse Floorplan & Zones
                </h2>
                <div style={{ display: "flex", gap: "0.5rem" }}>
                  <button 
                    type="button" 
                    className="btn btn-secondary btn-xs-text" 
                    onClick={handleExportPNG}
                    title="Export floorplan as PNG"
                  >
                    <Printer size={14} /> Export PNG
                  </button>
                  <button 
                    type="button" 
                    className="btn btn-secondary btn-xs-text" 
                    style={{ color: "var(--danger)", borderColor: "var(--danger)" }}
                    onClick={handleClearAllZones}
                    title="Wipe all zones and outlines"
                  >
                    <Trash2 size={14} /> Clear All
                  </button>
                </div>
              </div>
              
              <p className="text-secondary" style={{ fontSize: "0.85rem", lineHeight: "1.4", marginBottom: "0.5rem" }}>
                Draw polygons on the grid to map out your warehouse sections, aisles, and storage zones.
              </p>

              <div style={{ display: "flex", gap: "1rem", alignItems: "center", marginBottom: "1rem", padding: "1rem", backgroundColor: "var(--bg-input)", borderRadius: "8px" }}>
                <label style={{ color: "var(--text-secondary)", fontSize: "0.9rem" }}>Active Building:</label>
                <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                  <select 
                    className="form-input" 
                    style={{ width: "250px" }}
                    value={selectedBuildingId}
                    onChange={(e) => setSelectedBuildingId(e.target.value)}
                  >
                    {buildings.map(b => (
                      <option key={b.id} value={b.id}>{b.name}</option>
                    ))}
                  </select>
                  {selectedBuildingId && (
                    <>
                      <button 
                        type="button"
                        className="btn btn-secondary"
                        style={{ padding: "0.4rem" }}
                        onClick={() => {
                          const currentBuilding = buildings.find(b => b.id === selectedBuildingId);
                          const name = window.prompt("Enter new building name:", currentBuilding?.name);
                          if (name && name.trim()) {
                            updateBuilding(selectedBuildingId, { name: name.trim() });
                          }
                        }}
                        title="Edit Building Name"
                      >
                        <Edit size={16} />
                      </button>
                      <button 
                        type="button"
                        className="btn btn-secondary"
                        style={{ padding: "0.4rem", color: "var(--danger)", borderColor: "var(--danger)" }}
                        onClick={async () => {
                          const currentBuilding = buildings.find(b => b.id === selectedBuildingId);
                          if (window.confirm(`Are you sure you want to delete "${currentBuilding?.name}"? All its zones will also be deleted.`)) {
                            setZoneError("");
                            setZoneSuccess("");
                            try {
                              for (const zone of filteredZones) {
                                await deleteZone(zone.id);
                              }
                              await deleteBuilding(selectedBuildingId);
                              setSelectedBuildingId("");
                              setZoneSuccess("Building and its zones deleted.");
                              setTimeout(() => setZoneSuccess(""), 3000);
                            } catch (err) {
                              setZoneError("Failed to delete building.");
                            }
                          }
                        }}
                        title="Delete Building"
                      >
                        <Trash2 size={16} />
                      </button>
                    </>
                  )}
                </div>
                <button 
                  type="button"
                  className="btn btn-secondary btn-xs-text"
                  style={{ marginLeft: "auto" }}
                  onClick={async () => {
                    const name = window.prompt("Enter new building name:");
                    if (name) {
                      const id = await addBuilding({ name });
                      setSelectedBuildingId(id);
                    }
                  }}
                >
                  <Plus size={16} style={{marginRight: "4px"}} /> Add Building
                </button>
              </div>

              {zoneError && <div className="alert alert-error" style={{ marginBottom: "1rem" }}>{zoneError}</div>}
              {zoneSuccess && <div className="alert alert-success" style={{ marginBottom: "1rem" }}>{zoneSuccess}</div>}

              {zonesLoading ? (
                <div style={{ padding: "2rem", textAlign: "center", color: "var(--text-secondary)" }}>Loading floorplan...</div>
              ) : (
                <div className="grid-2" style={{ gridTemplateColumns: "1fr 300px", gap: "1.5rem", alignItems: "start" }}>
                  <div className="card glass-card" style={{ padding: "1rem", overflow: "hidden" }}>
                    <FloorplanEditor
                      zones={filteredZones}
                      selectedZoneId={editingZoneId}
                      onSelectZone={(id) => {
                        setEditingZoneId(id);
                        const z = filteredZones.find(x => x.id === id);
                        if (z) setEditingZoneName(z.name);
                      }}
                      onAddZone={async (points, isOutline) => {
                        try {
                          await addZone({ 
                            name: isOutline ? "Layout Outline" : `New Zone ${filteredZones.filter(z => !z.isOutline).length + 1}`, 
                            points, 
                            color: isOutline ? "none" : "hsl(215, 85%, 60%)",
                            isOutline: !!isOutline,
                            buildingId: selectedBuildingId
                          });
                          setZoneSuccess("Zone drawn successfully!");
                          setTimeout(() => setZoneSuccess(""), 3000);
                        } catch (err) {
                          setZoneError("Failed to save zone.");
                        }
                      }}
                      onUpdateZone={updateZone}
                      onDeleteZone={deleteZone}
                    />
                  </div>
                  
                  {/* Sidebar for Zone Properties */}
                  <div className="card glass-card" style={{ padding: "1.5rem" }}>
                    {editingZoneId ? (() => {
                      const selectedZ = filteredZones.find(x => x.id === editingZoneId);
                      const isSelectedOutline = selectedZ && selectedZ.isOutline;
                      
                      return (
                      <div>
                        <h3 style={{ fontSize: "1rem", fontWeight: "600", marginBottom: "1.5rem", color: "var(--text-primary)" }}>
                          {isSelectedOutline ? "Outline Properties" : "Zone Properties"}
                        </h3>
                        
                        {!isSelectedOutline && (
                          <>
                            <div className="form-group" style={{ marginBottom: "1rem" }}>
                              <label className="form-label">Zone Name</label>
                              <input
                                type="text"
                                className="form-input"
                                value={editingZoneName}
                                onChange={(e) => setEditingZoneName(e.target.value)}
                                style={{ backgroundColor: "var(--bg-input)" }}
                              />
                            </div>
                            
                            <div className="form-group" style={{ marginBottom: "1.5rem" }}>
                              <label className="form-label">Zone Color</label>
                              <div style={{ display: "flex", gap: "0.5rem" }}>
                                {["hsl(215, 85%, 60%)", "hsl(270, 85%, 60%)", "hsl(150, 85%, 45%)", "hsl(15, 85%, 60%)"].map(color => {
                                  const isSelectedColor = selectedZ && (selectedZ.color || "hsl(215, 85%, 60%)") === color;
                                  return (
                                    <button
                                      key={color}
                                      type="button"
                                      onClick={() => handleEditZoneProperties(editingZoneId, { color })}
                                      style={{
                                        width: "32px",
                                        height: "32px",
                                        borderRadius: "50%",
                                        backgroundColor: color,
                                        border: isSelectedColor ? "3px solid #ffffff" : "2px solid transparent",
                                        cursor: "pointer",
                                        boxShadow: isSelectedColor ? `0 0 8px ${color}` : "none"
                                      }}
                                    />
                                  );
                                })}
                              </div>
                            </div>
                          </>
                        )}

                        <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem", marginTop: isSelectedOutline ? "0" : "1.5rem" }}>
                          {!isSelectedOutline && (
                            <button 
                              type="button" 
                              className="btn btn-primary" 
                              onClick={() => {
                                if (selectedZ) handleEditZone(selectedZ.id, selectedZ.name);
                              }}
                            >
                              <Save size={16} /> Save Changes
                            </button>
                          )}
                          
                          <button 
                            type="button" 
                            className="btn btn-secondary" 
                            style={{ color: "var(--danger)", borderColor: "var(--danger)" }}
                            onClick={() => {
                              if (selectedZ) handleDeleteZone(selectedZ.id, selectedZ.name);
                            }}
                          >
                            <Trash2 size={16} /> Delete {isSelectedOutline ? "Outline" : "Zone"}
                          </button>
                        </div>
                      </div>
                    );})() : (
                      <div style={{ textAlign: "center", color: "var(--text-secondary)", marginTop: "2rem" }}>
                        <MousePointer2 size={32} style={{ opacity: 0.5, marginBottom: "1rem" }} />
                        <p style={{ fontSize: "0.9rem" }}>Select a zone on the floorplan to edit its properties.</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ) : activeTab === "invoicing" ? (
            <div className="settings-panel animate-fade-in" style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}>
              <div className="card glass-card">
                <div className="card-header">
                  <h3 className="card-title" style={{ display: "flex", alignItems: "center" }}>
                    <CreditCard size={18} style={{ marginRight: "0.5rem", color: "var(--primary)" }} />
                    Invoicing Settings
                  </h3>
                </div>
                
                {invoicingSuccess && <div className="alert alert-success" style={{ marginTop: "1rem" }}>{invoicingSuccess}</div>}
                {invoicingError && <div className="alert alert-danger" style={{ marginTop: "1rem" }}>{invoicingError}</div>}

                <form onSubmit={handleSaveInvoicing} style={{ marginTop: "1rem" }}>
                  <div className="form-group" style={{ maxWidth: "400px" }}>
                    <label className="form-label" htmlFor="nonCcDiscount">Non-CC Discount Percentage (%)</label>
                    <input
                      id="nonCcDiscount"
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      className="form-input"
                      value={nonCcDiscount}
                      onChange={(e) => setNonCcDiscount(parseFloat(e.target.value) || 0)}
                      required
                    />
                    <span className="text-secondary" style={{ fontSize: "0.75rem", marginTop: "0.35rem", display: "block" }}>
                      This discount will be subtracted from the PO markup rate if a non-credit card payment method is used.
                    </span>
                  </div>

                  <div style={{ display: "flex", gap: "1rem", marginTop: "1.5rem" }}>
                    <button type="submit" className="btn btn-primary">
                      <Save size={18} style={{ marginRight: "0.5rem" }} />
                      Save Invoicing Settings
                    </button>
                  </div>
                </form>
              </div>

              {/* Pay Schedule Management Card */}
              <div className="card glass-card">
                <div className="card-header">
                  <h3 className="card-title" style={{ display: "flex", alignItems: "center" }}>
                    <Sliders size={18} style={{ marginRight: "0.5rem", color: "var(--primary)" }} />
                    Pay Schedules
                  </h3>
                </div>
                
                {payScheduleSuccess && <div className="alert alert-success" style={{ marginTop: "1rem" }}>{payScheduleSuccess}</div>}
                {payScheduleError && <div className="alert alert-danger" style={{ marginTop: "1rem" }}>{payScheduleError}</div>}

                <p className="text-secondary" style={{ fontSize: "0.85rem", marginTop: "0.5rem" }}>
                  Add, edit, or remove pay schedules. Edits will update existing customer and vendor records automatically.
                </p>

                <form onSubmit={handleAddPaySchedule} style={{ display: "flex", gap: "1rem", marginTop: "1.25rem", alignItems: "flex-end", flexWrap: "wrap" }}>
                  <div className="form-group" style={{ margin: 0, flex: 1, minWidth: "200px" }}>
                    <label className="form-label">New Pay Schedule Name</label>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="e.g. Net 45, Bi-Weekly..."
                      value={newPayScheduleName}
                      onChange={(e) => setNewPayScheduleName(e.target.value)}
                    />
                  </div>
                  <button type="submit" className="btn btn-primary" style={{ height: "42px" }}>
                    <Plus size={16} style={{ marginRight: "0.25rem" }} /> Add Pay Schedule
                  </button>
                </form>

                <div className="categories-list" style={{ marginTop: "1.5rem", display: "flex", flexDirection: "column", gap: "0.75rem" }}>
                  {paySchedules.length === 0 ? (
                    <p style={{ fontStyle: "italic", color: "var(--text-muted)", fontSize: "0.95rem" }}>No pay schedules defined.</p>
                  ) : (
                    paySchedules.map((schedule) => (
                      <div key={schedule.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "0.75rem 1rem", border: "1px solid var(--border-color)", borderRadius: "6px", backgroundColor: "rgba(255,255,255,0.01)" }}>
                        {editingPayScheduleId === schedule.id ? (
                          <div style={{ display: "flex", gap: "0.75rem", flex: 1, alignItems: "center" }}>
                            <input
                              type="text"
                              className="form-input"
                              value={editingPayScheduleName}
                              onChange={(e) => setEditingPayScheduleName(e.target.value)}
                              style={{ margin: 0, padding: "0.35rem 0.5rem", fontSize: "0.9rem" }}
                              autoFocus
                            />
                            <button
                              type="button"
                              className="btn btn-primary"
                              onClick={() => handleEditPaySchedule(schedule.id, schedule.name)}
                              style={{ padding: "0.35rem 0.75rem", fontSize: "0.85rem" }}
                            >
                              Save
                            </button>
                            <button
                              type="button"
                              className="btn btn-secondary"
                              onClick={() => setEditingPayScheduleId(null)}
                              style={{ padding: "0.35rem 0.75rem", fontSize: "0.85rem" }}
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <>
                            <span style={{ fontWeight: 500, color: "var(--text-primary)" }}>{schedule.name}</span>
                            <div style={{ display: "flex", gap: "0.5rem" }}>
                              <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={() => {
                                  setEditingPayScheduleId(schedule.id);
                                  setEditingPayScheduleName(schedule.name);
                                }}
                                style={{ padding: "0.25rem 0.5rem", fontSize: "0.8rem" }}
                              >
                                Edit
                              </button>
                              <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={() => handleDeletePaySchedule(schedule.id, schedule.name)}
                                style={{ padding: "0.25rem 0.5rem", fontSize: "0.8rem", color: "var(--danger)", borderColor: "var(--danger)" }}
                              >
                                Delete
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          ) : activeTab === "bizdev" ? (
            <div className="settings-panel animate-fade-in" style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}>
              {/* Biz Dev Subtabs Navigation */}
              <div style={{ display: "flex", gap: "0.5rem", borderBottom: "1px solid var(--border-color)", paddingBottom: "0.5rem" }}>
                <button
                  type="button"
                  onClick={() => setBizDevSubTab("categories")}
                  className={`btn ${bizDevSubTab === "categories" ? "btn-primary" : "btn-secondary"}`}
                  style={{ padding: "0.4rem 1rem", fontSize: "0.85rem" }}
                >
                  <Tag size={15} style={{ marginRight: "0.4rem" }} /> Partner Categories
                </button>
                <button
                  type="button"
                  onClick={() => setBizDevSubTab("agreements")}
                  className={`btn ${bizDevSubTab === "agreements" ? "btn-primary" : "btn-secondary"}`}
                  style={{ padding: "0.4rem 1rem", fontSize: "0.85rem" }}
                >
                  <FileText size={15} style={{ marginRight: "0.4rem" }} /> Agreement Templates
                </button>
              </div>

              {bizDevSubTab === "categories" ? (
                /* Partner Categories Card */
                <div className="card glass-card">
                  <div className="card-header">
                    <h3 className="card-title" style={{ display: "flex", alignItems: "center" }}>
                      <Tag size={18} style={{ marginRight: "0.5rem", color: "var(--primary)" }} />
                      Partner Categories
                    </h3>
                  </div>
                  
                  {partnerCatSuccess && <div className="alert alert-success" style={{ marginTop: "1rem" }}>{partnerCatSuccess}</div>}
                  {partnerCatError && <div className="alert alert-danger" style={{ marginTop: "1rem" }}>{partnerCatError}</div>}

                  <p className="text-secondary" style={{ fontSize: "0.85rem", marginTop: "0.5rem" }}>
                    Add, edit, or remove partner categories used for classifying company partnerships in the Partners module.
                  </p>

                  <form onSubmit={handleAddPartnerCategory} style={{ display: "flex", gap: "1rem", marginTop: "1.25rem", alignItems: "flex-end", flexWrap: "wrap" }}>
                    <div className="form-group" style={{ margin: 0, flex: 1, minWidth: "200px" }}>
                      <label className="form-label">New Category Name</label>
                      <input
                        type="text"
                        className="form-input"
                        placeholder="e.g. Subcontractor, Teaming Partner..."
                        value={newPartnerCatName}
                        onChange={(e) => setNewPartnerCatName(e.target.value)}
                      />
                    </div>
                    <button type="submit" className="btn btn-primary" style={{ height: "42px" }}>
                      <Plus size={16} style={{ marginRight: "0.25rem" }} /> Add Category
                    </button>
                  </form>

                  <div className="categories-list" style={{ marginTop: "1.5rem", display: "flex", flexDirection: "column", gap: "0.75rem" }}>
                    {partnerCategories.length === 0 ? (
                      <p style={{ fontStyle: "italic", color: "var(--text-muted)", fontSize: "0.95rem" }}>No categories defined.</p>
                    ) : (
                      partnerCategories.map((category) => (
                        <div key={category.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "0.75rem 1rem", border: "1px solid var(--border-color)", borderRadius: "6px", backgroundColor: "rgba(255,255,255,0.01)" }}>
                          {editingPartnerCatId === category.id ? (
                            <div style={{ display: "flex", gap: "0.75rem", flex: 1, alignItems: "center" }}>
                              <input
                                type="text"
                                className="form-input"
                                value={editingPartnerCatName}
                                onChange={(e) => setEditingPartnerCatName(e.target.value)}
                                style={{ margin: 0, padding: "0.35rem 0.5rem", fontSize: "0.9rem" }}
                                autoFocus
                              />
                              <button
                                type="button"
                                className="btn btn-primary"
                                onClick={() => handleEditPartnerCategory(category.id, category.name)}
                                style={{ padding: "0.35rem 0.75rem", fontSize: "0.85rem" }}
                              >
                                Save
                              </button>
                              <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={() => setEditingPartnerCatId(null)}
                                style={{ padding: "0.35rem 0.75rem", fontSize: "0.85rem" }}
                              >
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <>
                              <span style={{ fontWeight: 500, color: "var(--text-primary)" }}>{category.name}</span>
                              <div style={{ display: "flex", gap: "0.5rem" }}>
                                <button
                                  type="button"
                                  className="btn btn-secondary"
                                  onClick={() => {
                                    setEditingPartnerCatId(category.id);
                                    setEditingPartnerCatName(category.name);
                                  }}
                                  style={{ padding: "0.25rem 0.5rem", fontSize: "0.8rem" }}
                                >
                                  Edit
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-secondary"
                                  onClick={() => handleDeletePartnerCategory(category.id, category.name)}
                                  style={{ padding: "0.25rem 0.5rem", fontSize: "0.8rem", color: "var(--danger)", borderColor: "var(--danger)" }}
                                >
                                  Delete
                                </button>
                              </div>
                            </>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                </div>
              ) : (
                <AgreementTemplatesSettings />
              )}
            </div>
          ) : activeTab === "companies" ? (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1.5rem" }}>
              {/* Add or Edit Company Card */}
              {editingCompany ? (
                <div className="card glass-card animate-fade-in">
                  <div className="card-header">
                    <h3 className="card-title" style={{ display: "flex", alignItems: "center" }}>
                      <Edit size={18} style={{ marginRight: "0.5rem", color: "var(--primary)" }} />
                      Edit Company: {editingCompany.name}
                    </h3>
                  </div>
                  <form onSubmit={handleUpdateCompany} style={{ marginTop: "1rem" }}>
                    <div className="form-group">
                      <label className="form-label" htmlFor="editCompanyName">Company Name</label>
                      <input
                        id="editCompanyName"
                        type="text"
                        className="form-input"
                        value={editCompanyName}
                        onChange={(e) => setEditCompanyName(e.target.value)}
                        placeholder="e.g. Acme Corporation"
                        required
                      />
                    </div>

                    <div className="form-group" style={{ marginTop: "1rem" }}>
                      <label className="form-label" htmlFor="editCompanyPrimaryColor">Primary Color</label>
                      <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
                        <input
                          id="editCompanyPrimaryColor"
                          type="color"
                          value={editCompanyPrimaryColor}
                          onChange={(e) => setEditCompanyPrimaryColor(e.target.value)}
                          style={{ width: "50px", height: "38px", border: "1px solid var(--border-color)", borderRadius: "4px", padding: 0, cursor: "pointer", backgroundColor: "transparent" }}
                        />
                        <input
                          type="text"
                          className="form-input"
                          value={editCompanyPrimaryColor}
                          onChange={(e) => setEditCompanyPrimaryColor(e.target.value)}
                          style={{ flex: 1, margin: 0 }}
                        />
                      </div>
                    </div>

                    <div className="form-group" style={{ marginTop: "1rem" }}>
                      <label className="form-label" htmlFor="editCompanyAddress">Address</label>
                      <input
                        id="editCompanyAddress"
                        type="text"
                        className="form-input"
                        value={editCompanyAddress}
                        onChange={(e) => setEditCompanyAddress(e.target.value)}
                        placeholder="123 Main St"
                      />
                    </div>

                    <div className="form-group" style={{ marginTop: "1rem" }}>
                      <label className="form-label" htmlFor="editCompanyAddress2">Address 2</label>
                      <input
                        id="editCompanyAddress2"
                        type="text"
                        className="form-input"
                        value={editCompanyAddress2}
                        onChange={(e) => setEditCompanyAddress2(e.target.value)}
                        placeholder="Suite 100"
                      />
                    </div>

                    <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr", gap: "1rem", marginTop: "1rem" }}>
                      <div className="form-group" style={{ margin: 0 }}>
                        <label className="form-label" htmlFor="editCompanyCity">City</label>
                        <input
                          id="editCompanyCity"
                          type="text"
                          className="form-input"
                          value={editCompanyCity}
                          onChange={(e) => setEditCompanyCity(e.target.value)}
                          placeholder="Houston"
                        />
                      </div>
                      <div className="form-group" style={{ margin: 0 }}>
                        <label className="form-label" htmlFor="editCompanyState">State</label>
                        <input
                          id="editCompanyState"
                          type="text"
                          className="form-input"
                          value={editCompanyState}
                          onChange={(e) => setEditCompanyState(e.target.value)}
                          placeholder="TX"
                        />
                      </div>
                      <div className="form-group" style={{ margin: 0 }}>
                        <label className="form-label" htmlFor="editCompanyZip">ZIP Code</label>
                        <input
                          id="editCompanyZip"
                          type="text"
                          className="form-input"
                          value={editCompanyZip}
                          onChange={(e) => setEditCompanyZip(e.target.value)}
                          placeholder="77001"
                        />
                      </div>
                    </div>

                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "1rem", marginTop: "1rem" }}>
                      <div className="form-group" style={{ margin: 0 }}>
                        <label className="form-label" htmlFor="editCompanyPhone">Phone</label>
                        <input
                          id="editCompanyPhone"
                          type="text"
                          className="form-input"
                          value={editCompanyPhone}
                          onChange={(e) => setEditCompanyPhone(e.target.value)}
                          placeholder="(555) 123-4567"
                        />
                      </div>
                      <div className="form-group" style={{ margin: 0 }}>
                        <label className="form-label" htmlFor="editCompanyEmail">Email</label>
                        <input
                          id="editCompanyEmail"
                          type="email"
                          className="form-input"
                          value={editCompanyEmail}
                          onChange={(e) => setEditCompanyEmail(e.target.value)}
                          placeholder="billing@company.com"
                        />
                      </div>
                      <div className="form-group" style={{ margin: 0 }}>
                        <label className="form-label" htmlFor="editCompanyWebsite">Website</label>
                        <input
                          id="editCompanyWebsite"
                          type="text"
                          className="form-input"
                          value={editCompanyWebsite}
                          onChange={(e) => setEditCompanyWebsite(e.target.value)}
                          placeholder="www.company.com"
                        />
                      </div>
                    </div>

                    {/* QuickBooks Configuration */}
                    <div style={{
                      marginTop: "1.25rem",
                      padding: "1rem",
                      backgroundColor: "rgba(59, 130, 246, 0.03)",
                      border: "1px solid var(--border-color)",
                      borderRadius: "6px"
                    }}>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "0.75rem" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                          <CreditCard size={16} style={{ color: "var(--primary)" }} />
                          <h4 style={{ margin: 0, fontSize: "0.9rem", fontWeight: 700, color: "var(--text-primary)" }}>
                            QuickBooks Online Configuration
                          </h4>
                        </div>
                        <span 
                          style={{ 
                            fontSize: "0.75rem", 
                            fontWeight: 600,
                            padding: "2px 8px", 
                            borderRadius: "12px",
                            backgroundColor: (editingCompany?.qbVerified || qbVerifyResult?.success) ? "rgba(16, 185, 129, 0.15)" : "rgba(245, 158, 11, 0.15)",
                            color: (editingCompany?.qbVerified || qbVerifyResult?.success) ? "#10b981" : "#f59e0b",
                            border: `1px solid ${(editingCompany?.qbVerified || qbVerifyResult?.success) ? "rgba(16, 185, 129, 0.3)" : "rgba(245, 158, 11, 0.3)"}`,
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "4px"
                          }}
                        >
                          <span style={{ width: "6px", height: "6px", borderRadius: "50%", backgroundColor: (editingCompany?.qbVerified || qbVerifyResult?.success) ? "#10b981" : "#f59e0b" }} />
                          {(editingCompany?.qbVerified || qbVerifyResult?.success) ? "Connected & Verified" : "Not Verified"}
                        </span>
                      </div>

                      <div className="form-group" style={{ marginBottom: "0.75rem" }}>
                        <label className="form-label" htmlFor="editCompanyQbId">
                          QB ID <span style={{ color: "var(--danger)" }}>*</span>
                        </label>
                        <input
                          id="editCompanyQbId"
                          type="text"
                          className="form-input"
                          value={editCompanyQbId}
                          onChange={(e) => setEditCompanyQbId(e.target.value)}
                          placeholder="e.g. AB1234567890..."
                          required
                        />
                      </div>

                      <div className="form-group" style={{ marginBottom: "0.75rem" }}>
                        <label className="form-label" htmlFor="editCompanyQbSecret">
                          QB Secret <span style={{ color: "var(--danger)" }}>*</span>
                        </label>
                        <div style={{ position: "relative" }}>
                          <input
                            id="editCompanyQbSecret"
                            type={showEditQbSecret ? "text" : "password"}
                            className="form-input"
                            value={editCompanyQbSecret}
                            onChange={(e) => setEditCompanyQbSecret(e.target.value)}
                            placeholder="Enter QuickBooks Client Secret"
                            style={{ paddingRight: "2.5rem" }}
                            required
                          />
                          <button
                            type="button"
                            onClick={() => setShowEditQbSecret(!showEditQbSecret)}
                            style={{
                              position: "absolute",
                              right: "8px",
                              top: "50%",
                              transform: "translateY(-50%)",
                              background: "none",
                              border: "none",
                              color: "var(--text-secondary)",
                              cursor: "pointer",
                              padding: "4px"
                            }}
                            title={showEditQbSecret ? "Hide Secret" : "Show Secret"}
                          >
                            {showEditQbSecret ? <EyeOff size={16} /> : <Eye size={16} />}
                          </button>
                        </div>
                      </div>

                      <div className="form-group" style={{ margin: "0 0 0.75rem 0" }}>
                        <label className="form-label" htmlFor="editCompanyQbCompanyId">
                          QB Company ID <span style={{ color: "var(--danger)" }}>*</span>
                        </label>
                        <input
                          id="editCompanyQbCompanyId"
                          type="text"
                          className="form-input"
                          value={editCompanyQbCompanyId}
                          onChange={(e) => setEditCompanyQbCompanyId(e.target.value)}
                          placeholder="e.g. 123456789012345 (Realm ID)"
                          required
                        />
                      </div>

                      <div className="form-group" style={{ margin: "0 0 0.75rem 0" }}>
                        <label className="form-label" htmlFor="editCompanyQbEnvironment">
                          QuickBooks Environment
                        </label>
                        <select
                          id="editCompanyQbEnvironment"
                          className="form-input"
                          value={editCompanyQbEnvironment}
                          onChange={(e) => setEditCompanyQbEnvironment(e.target.value)}
                        >
                          <option value="sandbox">Sandbox (Testing / Development)</option>
                          <option value="production">Production (Live)</option>
                        </select>
                      </div>

                      {/* Verify Connection Action */}
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: "0.5rem" }}>
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={handleVerifyQbConnection}
                          disabled={verifyingQb || !editCompanyQbId || !editCompanyQbSecret || !editCompanyQbCompanyId}
                          style={{
                            fontSize: "0.8rem",
                            padding: "0.4rem 0.75rem",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "0.4rem"
                          }}
                        >
                          <RefreshCw size={13} className={verifyingQb ? "animate-spin" : ""} />
                          {verifyingQb ? "Verifying Connection..." : "Verify Connection"}
                        </button>
                        {(editingCompany?.qbVerifiedAt || qbVerifyResult?.qbVerifiedAt) && (
                          <span style={{ fontSize: "0.75rem", color: "var(--text-secondary)" }}>
                            Verified: {new Date(editingCompany?.qbVerifiedAt || qbVerifyResult?.qbVerifiedAt).toLocaleDateString()} {new Date(editingCompany?.qbVerifiedAt || qbVerifyResult?.qbVerifiedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </span>
                        )}
                      </div>

                      {qbVerifyResult && (
                        <div 
                          style={{ 
                            marginTop: "0.75rem", 
                            padding: "0.6rem 0.75rem", 
                            borderRadius: "4px", 
                            fontSize: "0.8rem",
                            display: "flex",
                            alignItems: "center",
                            gap: "0.5rem",
                            backgroundColor: qbVerifyResult.success ? "rgba(16, 185, 129, 0.15)" : "rgba(239, 68, 68, 0.15)",
                            border: `1px solid ${qbVerifyResult.success ? "#10b981" : "#ef4444"}`,
                            color: qbVerifyResult.success ? "#10b981" : "#ef4444"
                          }}
                        >
                          {qbVerifyResult.success ? <CheckCircle2 size={16} /> : <AlertCircle size={16} />}
                          <span>{qbVerifyResult.message}</span>
                        </div>
                      )}
                    </div>

                    <div className="form-group" style={{ marginTop: "1.5rem" }}>
                      <label className="form-label">Assign Users</label>
                      <p className="text-secondary" style={{ fontSize: "0.8rem", marginBottom: "0.5rem" }}>
                        Select users who are authorized to view this company and its associated records. If none selected, visible to all.
                      </p>
                      <div style={{ 
                        maxHeight: "150px", 
                        overflowY: "auto", 
                        border: "1px solid var(--border-color)", 
                        borderRadius: "6px", 
                        padding: "0.75rem",
                        backgroundColor: "rgba(0,0,0,0.2)",
                        display: "flex",
                        flexDirection: "column",
                        gap: "0.5rem"
                      }}>
                        {users.filter(user => !user.deleted && user.status !== "Disabled").map((user) => {
                          const isChecked = editCompanyAssignedUsers.includes(user.id);
                          return (
                            <label key={user.id} style={{ display: "flex", alignItems: "center", gap: "0.5rem", cursor: "pointer", fontSize: "0.9rem", userSelect: "none" }}>
                              <input
                                type="checkbox"
                                checked={isChecked}
                                onChange={() => {
                                  setEditCompanyAssignedUsers(prev => 
                                    prev.includes(user.id) ? prev.filter(id => id !== user.id) : [...prev, user.id]
                                  );
                                }}
                                style={{ cursor: "pointer" }}
                              />
                              <span style={{ color: "var(--text-primary)" }}>{user.name || user.email || "Unnamed User"}</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>

                    <div className="form-group" style={{ marginTop: "1.25rem" }}>
                      <label style={{ display: "flex", alignItems: "center", gap: "0.5rem", cursor: "pointer", fontSize: "0.9rem", userSelect: "none" }}>
                        <input
                          type="checkbox"
                          checked={editCompanyIsActive}
                          onChange={(e) => setEditCompanyIsActive(e.target.checked)}
                          style={{ cursor: "pointer" }}
                        />
                        <span style={{ color: "var(--text-primary)", fontWeight: "500" }}>Set as Default active company logo</span>
                      </label>
                    </div>

                    <div className="form-group" style={{ marginTop: "1.5rem" }}>
                      <label className="form-label">Replace Logo Image (Optional)</label>
                      <div 
                        onClick={() => document.getElementById("edit-company-logo-input").click()}
                        style={{
                          border: "1.5px dashed var(--border-color)",
                          borderRadius: "8px",
                          padding: "1.5rem 1rem",
                          textAlign: "center",
                          cursor: "pointer",
                          backgroundColor: "rgba(255, 255, 255, 0.01)",
                          transition: "all 0.2s ease"
                        }}
                      >
                        <Plus size={20} style={{ color: "var(--text-secondary)", margin: "0 auto" }} />
                        <p style={{ margin: "0.25rem 0 0 0", fontSize: "0.8rem", color: "var(--text-secondary)" }}>
                          {editCompanyLogoFile ? `Selected: ${editCompanyLogoFile.name}` : "Click to upload replacement logo"}
                        </p>
                        <input
                          type="file"
                          accept="image/*"
                          id="edit-company-logo-input"
                          onChange={handleEditLogoUpload}
                          style={{ display: "none" }}
                        />
                      </div>
                    </div>

                    {successMsg && (
                      <div className="alert alert-success" style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginTop: "1rem", backgroundColor: "rgba(46, 204, 113, 0.15)", border: "1px solid #2ecc71", padding: "0.75rem", borderRadius: "4px" }}>
                        <Check size={16} className="text-success" />
                        <span>{successMsg}</span>
                      </div>
                    )}

                    <div style={{ marginTop: "1.5rem", display: "flex", gap: "1rem" }}>
                      <button type="submit" className="btn btn-primary" disabled={uploadingLogo} style={{ flex: 1 }}>
                        {uploadingLogo ? "Saving..." : "Save Changes"}
                      </button>
                      <button type="button" className="btn btn-secondary" onClick={() => setEditingCompany(null)} style={{ flex: 1 }}>
                        Cancel
                      </button>
                    </div>
                  </form>
                </div>
              ) : (
                <div className="card glass-card">
                  <div className="card-header">
                    <h3 className="card-title" style={{ display: "flex", alignItems: "center" }}>
                      <Plus size={18} style={{ marginRight: "0.5rem", color: "var(--primary)" }} />
                      Add New Company
                    </h3>
                  </div>
                  <form onSubmit={handleCreateCompany} style={{ marginTop: "1rem" }}>
                    <div className="form-group">
                      <label className="form-label" htmlFor="newCompanyName">Company Name</label>
                      <input
                        id="newCompanyName"
                        type="text"
                        className="form-input"
                        value={newCompanyName}
                        onChange={(e) => setNewCompanyName(e.target.value)}
                        placeholder="e.g. Acme Corporation"
                        required
                      />
                    </div>

                    <div className="form-group" style={{ marginTop: "1rem" }}>
                      <label className="form-label" htmlFor="newCompanyPrimaryColor">Primary Color</label>
                      <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
                        <input
                          id="newCompanyPrimaryColor"
                          type="color"
                          value={newCompanyPrimaryColor}
                          onChange={(e) => setNewCompanyPrimaryColor(e.target.value)}
                          style={{ width: "50px", height: "38px", border: "1px solid var(--border-color)", borderRadius: "4px", padding: 0, cursor: "pointer", backgroundColor: "transparent" }}
                        />
                        <input
                          type="text"
                          className="form-input"
                          value={newCompanyPrimaryColor}
                          onChange={(e) => setNewCompanyPrimaryColor(e.target.value)}
                          style={{ flex: 1, margin: 0 }}
                        />
                      </div>
                    </div>

                    <div className="form-group" style={{ marginTop: "1rem" }}>
                      <label className="form-label" htmlFor="newCompanyAddress">Address</label>
                      <input
                        id="newCompanyAddress"
                        type="text"
                        className="form-input"
                        value={newCompanyAddress}
                        onChange={(e) => setNewCompanyAddress(e.target.value)}
                        placeholder="123 Main St"
                      />
                    </div>

                    <div className="form-group" style={{ marginTop: "1rem" }}>
                      <label className="form-label" htmlFor="newCompanyAddress2">Address 2 (Suite/Floor)</label>
                      <input
                        id="newCompanyAddress2"
                        type="text"
                        className="form-input"
                        value={newCompanyAddress2}
                        onChange={(e) => setNewCompanyAddress2(e.target.value)}
                        placeholder="Suite 100"
                      />
                    </div>

                    <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr", gap: "1rem", marginTop: "1rem" }}>
                      <div className="form-group" style={{ margin: 0 }}>
                        <label className="form-label" htmlFor="newCompanyCity">City</label>
                        <input
                          id="newCompanyCity"
                          type="text"
                          className="form-input"
                          value={newCompanyCity}
                          onChange={(e) => setNewCompanyCity(e.target.value)}
                          placeholder="Houston"
                        />
                      </div>
                      <div className="form-group" style={{ margin: 0 }}>
                        <label className="form-label" htmlFor="newCompanyState">State</label>
                        <input
                          id="newCompanyState"
                          type="text"
                          className="form-input"
                          value={newCompanyState}
                          onChange={(e) => setNewCompanyState(e.target.value)}
                          placeholder="TX"
                        />
                      </div>
                      <div className="form-group" style={{ margin: 0 }}>
                        <label className="form-label" htmlFor="newCompanyZip">ZIP Code</label>
                        <input
                          id="newCompanyZip"
                          type="text"
                          className="form-input"
                          value={newCompanyZip}
                          onChange={(e) => setNewCompanyZip(e.target.value)}
                          placeholder="77001"
                        />
                      </div>
                    </div>

                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "1rem", marginTop: "1rem" }}>
                      <div className="form-group" style={{ margin: 0 }}>
                        <label className="form-label" htmlFor="newCompanyPhone">Phone</label>
                        <input
                          id="newCompanyPhone"
                          type="text"
                          className="form-input"
                          value={newCompanyPhone}
                          onChange={(e) => setNewCompanyPhone(e.target.value)}
                          placeholder="(555) 123-4567"
                        />
                      </div>
                      <div className="form-group" style={{ margin: 0 }}>
                        <label className="form-label" htmlFor="newCompanyEmail">Email</label>
                        <input
                          id="newCompanyEmail"
                          type="email"
                          className="form-input"
                          value={newCompanyEmail}
                          onChange={(e) => setNewCompanyEmail(e.target.value)}
                          placeholder="billing@company.com"
                        />
                      </div>
                      <div className="form-group" style={{ margin: 0 }}>
                        <label className="form-label" htmlFor="newCompanyWebsite">Website</label>
                        <input
                          id="newCompanyWebsite"
                          type="text"
                          className="form-input"
                          value={newCompanyWebsite}
                          onChange={(e) => setNewCompanyWebsite(e.target.value)}
                          placeholder="www.company.com"
                        />
                      </div>
                    </div>

                    {/* QuickBooks Configuration */}
                    <div style={{
                      marginTop: "1.25rem",
                      padding: "1rem",
                      backgroundColor: "rgba(59, 130, 246, 0.03)",
                      border: "1px solid var(--border-color)",
                      borderRadius: "6px"
                    }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.75rem" }}>
                        <CreditCard size={16} style={{ color: "var(--primary)" }} />
                        <h4 style={{ margin: 0, fontSize: "0.9rem", fontWeight: 700, color: "var(--text-primary)" }}>
                          QuickBooks Online Configuration
                        </h4>
                      </div>

                      <div className="form-group" style={{ marginBottom: "0.75rem" }}>
                        <label className="form-label" htmlFor="newCompanyQbId">
                          QB ID <span style={{ color: "var(--danger)" }}>*</span>
                        </label>
                        <input
                          id="newCompanyQbId"
                          type="text"
                          className="form-input"
                          value={newCompanyQbId}
                          onChange={(e) => setNewCompanyQbId(e.target.value)}
                          placeholder="e.g. AB1234567890..."
                          required
                        />
                      </div>

                      <div className="form-group" style={{ marginBottom: "0.75rem" }}>
                        <label className="form-label" htmlFor="newCompanyQbSecret">
                          QB Secret <span style={{ color: "var(--danger)" }}>*</span>
                        </label>
                        <div style={{ position: "relative" }}>
                          <input
                            id="newCompanyQbSecret"
                            type={showNewQbSecret ? "text" : "password"}
                            className="form-input"
                            value={newCompanyQbSecret}
                            onChange={(e) => setNewCompanyQbSecret(e.target.value)}
                            placeholder="Enter QuickBooks Client Secret"
                            style={{ paddingRight: "2.5rem" }}
                            required
                          />
                          <button
                            type="button"
                            onClick={() => setShowNewQbSecret(!showNewQbSecret)}
                            style={{
                              position: "absolute",
                              right: "8px",
                              top: "50%",
                              transform: "translateY(-50%)",
                              background: "none",
                              border: "none",
                              color: "var(--text-secondary)",
                              cursor: "pointer",
                              padding: "4px"
                            }}
                            title={showNewQbSecret ? "Hide Secret" : "Show Secret"}
                          >
                            {showNewQbSecret ? <EyeOff size={16} /> : <Eye size={16} />}
                          </button>
                        </div>
                      </div>

                      <div className="form-group" style={{ margin: "0 0 0.75rem 0" }}>
                        <label className="form-label" htmlFor="newCompanyQbCompanyId">
                          QB Company ID <span style={{ color: "var(--danger)" }}>*</span>
                        </label>
                        <input
                          id="newCompanyQbCompanyId"
                          type="text"
                          className="form-input"
                          value={newCompanyQbCompanyId}
                          onChange={(e) => setNewCompanyQbCompanyId(e.target.value)}
                          placeholder="e.g. 123456789012345 (Realm ID)"
                          required
                        />
                      </div>

                      <div className="form-group" style={{ margin: 0 }}>
                        <label className="form-label" htmlFor="newCompanyQbEnvironment">
                          QuickBooks Environment
                        </label>
                        <select
                          id="newCompanyQbEnvironment"
                          className="form-input"
                          value={newCompanyQbEnvironment}
                          onChange={(e) => setNewCompanyQbEnvironment(e.target.value)}
                        >
                          <option value="sandbox">Sandbox (Testing / Development)</option>
                          <option value="production">Production (Live)</option>
                        </select>
                      </div>
                    </div>

                    <div className="form-group" style={{ marginTop: "1.5rem" }}>
                      <label className="form-label">Assign Users</label>
                      <p className="text-secondary" style={{ fontSize: "0.8rem", marginBottom: "0.5rem" }}>
                        Select users who are authorized to view this company and its associated records. If none selected, visible to all.
                      </p>
                      <div style={{ 
                        maxHeight: "150px", 
                        overflowY: "auto", 
                        border: "1px solid var(--border-color)", 
                        borderRadius: "6px", 
                        padding: "0.75rem",
                        backgroundColor: "rgba(0,0,0,0.2)",
                        display: "flex",
                        flexDirection: "column",
                        gap: "0.5rem"
                      }}>
                        {users.filter(user => !user.deleted && user.status !== "Disabled").map((user) => {
                          const isChecked = newCompanyAssignedUsers.includes(user.id);
                          return (
                            <label key={user.id} style={{ display: "flex", alignItems: "center", gap: "0.5rem", cursor: "pointer", fontSize: "0.9rem", userSelect: "none" }}>
                              <input
                                type="checkbox"
                                checked={isChecked}
                                onChange={() => {
                                  setNewCompanyAssignedUsers(prev => 
                                    prev.includes(user.id) ? prev.filter(id => id !== user.id) : [...prev, user.id]
                                  );
                                }}
                                style={{ cursor: "pointer" }}
                              />
                              <span style={{ color: "var(--text-primary)" }}>{user.name || user.email || "Unnamed User"}</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>

                    <div className="form-group" style={{ marginTop: "1.25rem" }}>
                      <label style={{ display: "flex", alignItems: "center", gap: "0.5rem", cursor: "pointer", fontSize: "0.9rem", userSelect: "none" }}>
                        <input
                          type="checkbox"
                          checked={newCompanyIsActive}
                          onChange={(e) => setNewCompanyIsActive(e.target.checked)}
                          style={{ cursor: "pointer" }}
                        />
                        <span style={{ color: "var(--text-primary)", fontWeight: "500" }}>Set as Default active company logo</span>
                      </label>
                    </div>

                    <div className="form-group" style={{ marginTop: "1.5rem" }}>
                      <label className="form-label">Company Logo Image</label>
                      <div 
                        onClick={() => document.getElementById("company-logo-input").click()}
                        style={{
                          border: "1.5px dashed var(--border-color)",
                          borderRadius: "8px",
                          padding: "2rem",
                          textAlign: "center",
                          cursor: "pointer",
                          backgroundColor: "rgba(255, 255, 255, 0.01)",
                          transition: "all 0.2s ease"
                        }}
                      >
                        <Plus size={24} style={{ color: "var(--text-secondary)", margin: "0 auto" }} />
                        <p style={{ margin: "0.5rem 0 0 0", fontSize: "0.85rem", color: "var(--text-secondary)" }}>
                          {newCompanyLogoFile ? `Selected: ${newCompanyLogoFile.name}` : "Click to select or upload logo"}
                        </p>
                        <input
                          type="file"
                          accept="image/*"
                          id="company-logo-input"
                          onChange={handleLogoUpload}
                          style={{ display: "none" }}
                        />
                      </div>
                    </div>

                    {successMsg && (
                      <div className="alert alert-success" style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginTop: "1rem", backgroundColor: "rgba(46, 204, 113, 0.15)", border: "1px solid #2ecc71", padding: "0.75rem", borderRadius: "4px" }}>
                        <Check size={16} className="text-success" />
                        <span>{successMsg}</span>
                      </div>
                    )}

                    <div style={{ marginTop: "1.5rem" }}>
                      <button type="submit" className="btn btn-primary" disabled={uploadingLogo}>
                        {uploadingLogo ? "Uploading..." : "Add Company"}
                      </button>
                    </div>
                  </form>
                </div>
              )}

              {/* Companies List Card */}
              <div className="card glass-card">
                <div className="card-header">
                  <h3 className="card-title" style={{ display: "flex", alignItems: "center" }}>
                    <Sliders size={18} style={{ marginRight: "0.5rem", color: "var(--primary)" }} />
                    Registered Companies
                  </h3>
                </div>
                <div style={{ marginTop: "1rem" }}>
                  <p className="text-secondary" style={{ fontSize: "0.85rem", marginBottom: "1rem" }}>
                    Click on a company card to load its details and settings on the left side.
                  </p>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))", gap: "1rem" }}>
                    {allCompanyLogos.map((logo) => (
                      <div key={logo.id} style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                        <div 
                          style={{ 
                            position: "relative",
                            width: "120px",
                            height: "120px",
                            borderRadius: "8px",
                            border: logo.isActive 
                              ? `2.5px solid ${logo.primaryColor || "var(--primary)"}` 
                              : "1.5px solid var(--border-color)",
                            overflow: "hidden",
                            backgroundColor: "rgba(255, 255, 255, 0.02)",
                            cursor: "pointer",
                            boxShadow: logo.isActive 
                              ? `0 0 10px ${logo.primaryColor || "var(--primary)"}` 
                              : "none",
                            transition: "all 0.2s ease"
                          }}
                          onClick={() => handleStartEdit(logo)}
                          title={logo.name ? `Company: ${logo.name} (Click to edit)` : "Click to edit"}
                        >
                          <img src={logo.url} alt={logo.name || "Company logo"} style={{ width: "100%", height: "100%", objectFit: "contain", padding: "0.5rem" }} />
                          {logo.isActive && (
                            <div 
                              style={{ 
                                position: "absolute",
                                top: "4px",
                                right: "4px",
                                background: logo.primaryColor || "var(--primary)",
                                borderRadius: "50%",
                                width: "20px",
                                height: "20px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                border: "1px solid #ffffff"
                              }}
                            >
                              <Check size={12} style={{ color: "#ffffff" }} />
                            </div>
                          )}
                          {logo.primaryColor && (
                            <div 
                              style={{ 
                                position: "absolute",
                                top: "4px",
                                left: "4px",
                                background: logo.primaryColor,
                                borderRadius: "50%",
                                width: "16px",
                                height: "16px",
                                border: "1px solid #ffffff",
                                boxShadow: "0 1px 3px rgba(0,0,0,0.3)"
                              }}
                              title={`Primary color: ${logo.primaryColor}`}
                            />
                          )}
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleStartEdit(logo);
                            }}
                            style={{
                              position: "absolute",
                              bottom: "4px",
                              left: "4px",
                              background: "rgba(0,0,0,0.6)",
                              border: "none",
                              borderRadius: "4px",
                              padding: "4px",
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center"
                            }}
                            title="Edit Company"
                          >
                            <Edit size={14} style={{ color: "#ffffff" }} />
                          </button>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemoveLogo(logo.id);
                            }}
                            style={{
                              position: "absolute",
                              bottom: "4px",
                              right: "4px",
                              background: "rgba(0,0,0,0.6)",
                              border: "none",
                              borderRadius: "4px",
                              padding: "4px",
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center"
                            }}
                            title="Delete Company"
                          >
                            <Trash2 size={14} style={{ color: "var(--danger)" }} />
                          </button>
                        </div>
                        <span 
                          style={{ 
                            fontSize: "0.8rem", 
                            color: logo.isActive ? "var(--primary)" : "var(--text-secondary)", 
                            marginTop: "0.5rem",
                            textAlign: "center",
                            maxWidth: "120px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            fontWeight: logo.isActive ? "bold" : "normal"
                          }}
                          title={logo.name}
                        >
                          {logo.name || "Unnamed"}
                        </span>
                        <span 
                          style={{ 
                            fontSize: "0.7rem", 
                            color: (logo.qbVerified || logo.qbCompanyId) ? "#10b981" : "#f59e0b",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "4px",
                            marginTop: "2px"
                          }}
                        >
                          <span style={{ width: "5px", height: "5px", borderRadius: "50%", backgroundColor: (logo.qbVerified || logo.qbCompanyId) ? "#10b981" : "#f59e0b" }} />
                          {(logo.qbVerified || logo.qbCompanyId) ? "QB Connected" : "QB Missing"}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ) : null}

        </div>

        {/* Right Column Wrapper */}
        {activeTab === "pos" && (
          <div style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}>
            {/* Reset Preferences card */}
            <div className="card glass-card">
              <div className="card-header">
                <h3 className="card-title" style={{ display: "flex", alignItems: "center" }}>
                  <RefreshCw size={18} style={{ marginRight: "0.5rem", color: "var(--danger)" }} />
                  Reset Preferences
                </h3>
              </div>
              <p className="text-secondary" style={{ fontSize: "0.875rem", marginTop: "0.5rem", lineHeight: "1.4" }}>
                Reset table layouts (such as dragged column arrangements) back to standard factory settings.
              </p>
              <button 
                type="button" 
                className="btn btn-secondary" 
                style={{ marginTop: "1rem", borderColor: "var(--danger)", color: "var(--danger)" }}
                onClick={handleResetColumns}
              >
                Reset Estimates Columns
              </button>
            </div>
          </div>
        )}
      </div>
      <style>{`
        .tag-suggestions-dropdown .suggestion-item:hover {
          background-color: var(--primary-hover) !important;
          color: #ffffff !important;
        }
      `}</style>
    </div>
  );
}
